import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { combineLatest, forkJoin, interval, of, Subject, Subscription, throwError, timer } from 'rxjs'
import { catchError, filter, finalize, first, shareReplay, switchMap, takeUntil, tap } from 'rxjs/operators'
import { visibilityAwareTimer } from 'src/utils/visibility-aware-timer'
import { ApiService } from 'src/app/services/api.service'
import { CrudAction, CrudEvent } from 'src/utils/crud-subject'
import { MenuItem } from 'primeng/api'
import {
  FailoverTestResultDto,
  ResourceScope,
  RestoreTriggerDto,
  RestoreTypeDto,
  SyncPhaseDto,
} from 'src/app/services/dtos'
import { UiService } from 'src/app/services/ui.service'
import { ModalDialogComponent } from 'src/app/shared/modal-dialog/modal-dialog.component'
import { ApplicationResource, FailbackResource } from 'src/app/shared/resource-viewer-base'
import {
  durationText,
  getResourceNameFromArn,
  getResourceTypeDisplayString,
  shortTimeString,
  timeAgoString,
} from 'src/app/shared/utils'
import { handleArpioError } from 'src/utils/handle-error'
import { ObjectErrorHostDirective } from 'src/app/shared/directives/object-error-host.directive'
import { ErrorAction } from 'src/utils/presented-error'

import { subscriptionActive } from 'src/utils/rxjs-utils'
import { AccountService } from 'src/app/account/services/account.service'
import { Application, ApplicationProviderService } from 'src/app/account/services/application.service'
import { ConcludeTestSettings } from 'src/app/account/components/conclude-test-dialog/conclude-test-dialog.component'
import { FailbackSettings } from 'src/app/account/components/failback-dialog/failback-dialog.component'
import {
  AppStatusState,
  computeProcessStatus,
  ProcessingStatus,
} from 'src/app/account/components/process-state/process-state.component'
import { RelevantStatus } from 'src/app/shared/types'
import {
  BackupHealthStatus,
  computeBackupHealthStatus,
  computeFailbackStatus,
  computeRecoveryEnvStatus,
  FailbackStatus,
  RecoveryEnvironmentStatus,
} from 'src/app/account/components/application/statusText'
import { Http409Error } from 'src/utils/errors'
import { LaunchRestoreSettings } from 'src/app/account/components/launch-sync-dialog-base'
import {
  AppResourcesViewComponent,
  isStatusQuarantineType,
  ResourceStatus,
  StatusContext,
  ViewableApplicationResource,
} from 'src/app/account/components/app-resources-view/app-resources-view.component'
import { ViewableIssue, makeViewableIssues } from 'src/app/account/components/issues-view/issues-view.component'
import { LoggerService } from '../../services/logger.service'
import { AccessLevelService, AppPermission } from 'src/app/services/access-level.service'
import { ResourcesTab } from './resources-tab'
import { CopyFeedbackService } from 'src/app/shared/copy-feedback.service'
import { CrossTabSyncService } from 'src/app/services/cross-tab-sync.service'

// A blob download named with the wrong extension won't open, so take the report's real
// one off the presigned URL — `.zip` when the server bundled both variants.
function reportFileExtension(presignedUrl: string): string {
  const objectName = presignedUrl.split(/[?#]/)[0]
  return /\.zip$/i.test(objectName) ? 'zip' : 'pdf'
}

/** Exported so the test page previews the same set the overview really runs. */
export const APPLICATION_OVERVIEW_ERROR_ACTIONS = ['refresh']

@Component({
  selector: 'app-application',
  templateUrl: './application.component.html',
  styleUrls: ['./application.component.scss'],
  standalone: false,
  // OnPush so hover/scroll/timer events don't cycle CD through this component's heavy
  // template (status bars, tabs, action buttons) on every Zone-patched event. The data-
  // arrival paths (setApplication and a few subscribe/setTimeout callbacks) explicitly
  // call markForCheck() — see those sites for the wiring.
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [CopyFeedbackService],
})
export class ApplicationComponent implements OnInit, OnDestroy, AfterViewChecked, OnChanges {
  static APPLICATION_REFRESH_INTERVAL = 60 * 1000 // 1 minute (standby)
  static RAPID_REFRESH_INTERVAL = 2000 // 2 seconds (active test/recovery)

  pendingRecoverySettings: LaunchRestoreSettings[] | null = null
  showAreYouSure: boolean = false
  application: Application = null
  selectedApplications: Application[] = []
  quarantineEnabled: boolean = false

  _recoveryEnvStatus: RecoveryEnvironmentStatus = null
  _backupHealthStatus: BackupHealthStatus = null
  _failbackStatus: FailbackStatus = null

  // Default tab for the app overview page
  currentTab = ResourcesTab.BACKUP

  filterText = ''
  chartFullscreen = false
  private fragmentSubscription: any = null

  // Cached derived values — updated in recomputeTabStatuses() / setApplication() to avoid
  // recalculation on every change detection cycle
  _cachedBackupIssues: ViewableIssue[] = []
  _cachedRestoreIssues: ViewableIssue[] = []
  _cachedFailbackIssues: ViewableIssue[] = []
  _cachedHasIssues: boolean = false
  _cachedTotalIssueCount: number = 0
  private _issuesEverSeen: boolean = false // tracks if issues existed for this app; prevents transient empty redirect
  _cachedHasRecoveryResources: boolean = false
  _cachedHasBackupResources: boolean = false
  _cachedResourcesAreQuarantined: boolean = false
  _cachedHasFailbackResources: boolean = false
  _cachedHasRestorePermission: boolean = false
  _cachedLastBackupStatus: ProcessingStatus = null
  _cachedLastRestoreStatus: ProcessingStatus = null
  _cachedLastFailback: ProcessingStatus = null

  // Tab requested by URL fragment before application data has loaded
  private pendingTab: ResourcesTab = null
  // Deferred issue deep-link state: ngAfterViewChecked retries scroll until the DOM element exists
  private pendingScrollToIssueId: string = null
  private pendingHighlightIssueIds: string[] = []
  private hasScrolledToIssue = false
  private scrollToIssueAttempts = 0
  // Deferred scope-section scroll: set when entering the Issues tab from a status bar
  // "Review and fix N issues" link — we scroll to the scope heading rather than a specific issue.
  private pendingScrollToScope: 'backup' | 'recovery' | 'failback' | null = null
  private hasScrolledToScope = false
  private scrollToScopeAttempts = 0

  /** Tier 3 host for state conflicts about this application. Read for `blockedReason`. */
  @ViewChild(ObjectErrorHostDirective)
  private overviewErrorHost: ObjectErrorHostDirective

  /** Remedies this page can run; anything else is dropped rather than rendered as a dead button. */
  readonly overviewErrorActions = APPLICATION_OVERVIEW_ERROR_ACTIONS

  @ViewChild('failoverDialog', { static: true })
  failoverDialog: ModalDialogComponent
  @ViewChild('concludeFailoverDialog', { static: true })
  concludeFailoverDialog: ModalDialogComponent
  @ViewChild('testFailoverDialog', { static: true })
  testFailoverDialog: ModalDialogComponent
  @ViewChild('sandboxConfigDialog', { static: true })
  sandboxConfigDialog: ModalDialogComponent
  @ViewChild('concludeTestFailoverDialog', { static: true })
  concludeTestFailoverDialog: ModalDialogComponent
  @ViewChild('failbackDialog', { static: true })
  failbackDialog: ModalDialogComponent
  @ViewChild('unquarantineDialog', { static: true })
  unquarantineDialog: ModalDialogComponent
  @ViewChild('awsEventLogsDialog', { static: true })
  awsEventLogsDialog: ModalDialogComponent
  @ViewChild('recoveryResourcesView')
  recoveryResourcesView: AppResourcesViewComponent
  @ViewChild('backupResourcesView')
  backupResourcesView: AppResourcesViewComponent
  @ViewChild('failbackResourcesView')
  failbackResourcesView: AppResourcesViewComponent

  // Auto-focus close button when RPO fullscreen overlay opens for keyboard accessibility.
  // Setter fires each time the *ngIf creates/destroys the element.
  @ViewChild('chartFullscreenCloseBtn')
  set chartFullscreenCloseBtn(btn: ElementRef<HTMLButtonElement> | undefined) {
    if (btn) {
      // Defer focus so the DOM is fully rendered before we move focus
      setTimeout(() => btn.nativeElement.focus())
    }
  }
  readonly Tab = ResourcesTab
  readonly StatusContext = StatusContext
  readonly RelevantStatus = RelevantStatus

  // Destroy subject for proper subscription cleanup on app navigation
  private destroy$ = new Subject<void>()

  private applicationIdSubscription: Subscription = null
  private applicationSubscription: Subscription = null
  private forceRefreshSubscription: Subscription = null
  private launchSyncSubscription: Subscription = null
  private launchFailbackSubscription: Subscription = null
  private applicationRefreshTimerSubscription: Subscription = null
  private timerIntegrityCheckSubscription: Subscription = null

  private rapidRefreshExpires: Date = null
  // This boolean allows us to handle the case where we just changed the ApplicationDto, but
  // due to the replication lag DynamoDB Global Tables, an immediate refresh
  // will fetch stale data. This boolean causes us to skip setting the ApplicationDto for the
  // first refresh. Subsequent refreshes (i.e. after 5s) will do a normal refresh.
  private delayRapidRefresh: boolean = false
  // This boolean forces a refresh of resources when navigating from the editor
  private forceRefreshResources: boolean = false
  // Track when a background poll is actively fetching data (for spinner visualization)
  isPolling: boolean = false
  // Track when the user manually clicked refresh (for button spinner only)
  manualRefreshing: boolean = false
  applicationId: string
  // Track when resources are being refreshed after navigating from the editor
  resourcesRefreshing: boolean = false
  // True during tab switch until the new scope's resources arrive (shows skeleton rows)
  tabTransitionLoading: boolean = false
  // Optimistic flag: set after launching test/recovery, cleared when backend confirms non-STANDBY phase
  restoreLaunching: boolean = false
  readonly skeletonPlaceholderRows = new Array(10).fill({}) as any[]
  private tabTransitionTimeoutSubscription: Subscription = null
  private resourcesRefreshTimeoutSubscription: Subscription = null
  // True while a navigation-triggered refresh is in flight for a cached app, so we show a
  // loading state instead of the previous visit's frozen recovery data (RPO chart + backup
  // timestamp) until fresh data arrives.
  recoveryDataRefreshing: boolean = false
  private recoveryDataRefreshTimeoutSubscription: Subscription = null

  // Cached arrays prevent getResources() from returning new references every change detection cycle.
  // Refreshed on every setApplication() call to stay in sync with tab status counts.
  cachedRecoveryResources: ApplicationResource[] = []
  cachedBackupResources: ApplicationResource[] = []
  cachedFailbackResources: FailbackResource[] = []

  // Permission states
  private canTestPermission = false
  private canRecoverPermission = false
  private canManagePermission = false
  private permissionsSubscription: Subscription = null

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private accountService: AccountService,
    private applicationProviderService: ApplicationProviderService,
    private apiService: ApiService,
    private uiService: UiService,
    private ref: ChangeDetectorRef,
    private logger: LoggerService,
    private accessLevelService: AccessLevelService,
    private crossTabSync: CrossTabSyncService,
    public copyFeedback: CopyFeedbackService
  ) {}

  ngOnInit() {
    // Initialize permissions
    this.initializePermissions()

    // When the account ID or application ID change, re-initialize the application.
    // We don't read the account ID from the URL because we want to wait for the DTO
    // to load.  We need it throughout for the multi-account flag.  Instead, we listen
    // to the accountIdStream on the account service which emits account IDs when they change.
    this.applicationIdSubscription = combineLatest([this.accountService.accountIdStream, this.route.paramMap])
      .pipe(finalize(() => (this.applicationIdSubscription = null)))
      .subscribe(([accountId, paramMap]) => {
        const newApplicationId = paramMap.get('applicationId')!
        // Cancel old poll subscriptions immediately when applicationId changes to prevent stale emissions
        // from being processed after the app has switched. This must happen BEFORE the new app loads.
        if (this.applicationId && this.applicationId !== newApplicationId) {
          // Create new Subject FIRST to avoid race condition where subscriptions start before new Subject exists
          const oldDestroy = this.destroy$
          this.destroy$ = new Subject<void>()
          // THEN emit on old subject to kill existing subscriptions
          oldDestroy.next()
          oldDestroy.complete()
          this.cancelSubscriptionsInCorrectOrder()
        }
        this.applicationId = newApplicationId
        // Update permissions for this specific application
        this.updatePermissionsForApplication(this.applicationId)
        this.initializeApplication(accountId, newApplicationId)
        this.ref.markForCheck() // OnPush: route-driven entry point
      })

    // Sync current tab from URL fragment on navigation (including page reload).
    // Uses switchTab() to ensure scope activation and resource cache refresh,
    // but skips if the tab is already current (avoids echo from programmatic
    // router.navigate calls in switchTab itself).
    this.fragmentSubscription = this.route.fragment.subscribe((fragment) => {
      if (!fragment) {
        this.pendingTab = null
        this.ref.markForCheck()
        return
      }
      const tab = ApplicationComponent.fragmentToTab(fragment)
      if (tab != null) {
        this.pendingTab = tab
        if (this.currentTab !== tab) {
          this.switchTab(tab)
        }
      } else {
        // Unknown fragment — treat as an issue ID for deep-linking to a specific issue
        this.pendingTab = ResourcesTab.ISSUES
        if (this.currentTab !== ResourcesTab.ISSUES) {
          this.switchTab(ResourcesTab.ISSUES)
        }
        this.pendingScrollToIssueId = fragment
        this.pendingHighlightIssueIds = [fragment]
        this.hasScrolledToIssue = false
        this.scrollToIssueAttempts = 0
      }
      this.ref.markForCheck() // OnPush: URL fragment changes drive tab/issue state
    })
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('applicationId' in changes && !changes.applicationId.firstChange) {
      // Create new destroy$ BEFORE emitting on the old one to prevent race conditions
      const oldDestroy$ = this.destroy$
      this.destroy$ = new Subject<void>()

      // Emit on old destroy$ to kill all apps subscriptions
      oldDestroy$.next()
      oldDestroy$.complete()

      this.cancelSubscriptionsInCorrectOrder()
    }
  }

  ngOnDestroy() {
    this.destroy$.next()
    this.destroy$.complete()

    if (subscriptionActive(this.applicationIdSubscription)) {
      this.applicationIdSubscription.unsubscribe()
      this.applicationIdSubscription = null
    }

    if (subscriptionActive(this.permissionsSubscription)) {
      this.permissionsSubscription.unsubscribe()
      this.permissionsSubscription = null
    }

    if (this.fragmentSubscription) {
      this.fragmentSubscription.unsubscribe()
      this.fragmentSubscription = null
    }

    this.cleanup()
  }

  private initializePermissions() {
    // We need to wait for the application ID to be available
    // The actual permission check will be done when initializing the application
    this.canTestPermission = false
    this.canRecoverPermission = false
    this.canManagePermission = false
  }

  private updatePermissionsForApplication(applicationId: string) {
    if (this.permissionsSubscription) {
      this.permissionsSubscription.unsubscribe()
    }

    // Check permissions directly using AppPermission enums
    this.permissionsSubscription = combineLatest([
      this.accessLevelService.hasAppPermission(applicationId, AppPermission.MANAGE_TEST),
      this.accessLevelService.hasAppPermission(applicationId, AppPermission.MANAGE_RECOVERY),
      this.accessLevelService.hasAppPermission(applicationId, AppPermission.UPDATE_APP),
    ]).subscribe(([canTest, canRecover, canManage]) => {
      this.canTestPermission = canTest
      this.canRecoverPermission = canRecover
      this.canManagePermission = canManage
      this.ref.markForCheck()
    })
  }

  get recoveryPointAttempts() {
    return this.application.recoveryPointAttempts || []
  }

  private cancelApplicationSubscription() {
    if (subscriptionActive(this.applicationSubscription)) {
      this.applicationSubscription.unsubscribe()
    }
    this.applicationSubscription = null
  }

  private cancelApplicationRefreshTimerSubscription() {
    if (this.applicationRefreshTimerSubscription) {
      this.applicationRefreshTimerSubscription.unsubscribe()
    }
    this.applicationRefreshTimerSubscription = null
  }

  private cancelSubscriptionsInCorrectOrder() {
    // This helper function will cancel the application subscription and the refresh timer.  These
    // need to happen in a specific order, so we have this 1 function to get it right.

    // In most places where we create a new application subscription, we set up a finalize() block
    // that will reschedule the refresh timer once the application subscription completes.
    // If we want both subscriptions canceled, but we cancel the application subscription after
    // we cancel the refresh subscription, the finalize block will just reschedule the refresh
    // subscription that we just canceled.

    // Always cancel the application subscription first.

    this.cancelApplicationSubscription()
    this.cancelApplicationRefreshTimerSubscription()
  }

  private cancelTabTransitionTimeout() {
    if (subscriptionActive(this.tabTransitionTimeoutSubscription)) {
      this.tabTransitionTimeoutSubscription.unsubscribe()
      this.tabTransitionTimeoutSubscription = null
    }
  }

  private cleanup() {
    this.cancelSubscriptionsInCorrectOrder()

    if (subscriptionActive(this.timerIntegrityCheckSubscription)) {
      this.timerIntegrityCheckSubscription.unsubscribe()
      this.timerIntegrityCheckSubscription = null
    }

    if (subscriptionActive(this.forceRefreshSubscription)) {
      this.forceRefreshSubscription.unsubscribe()
      this.forceRefreshSubscription = null
    }

    if (subscriptionActive(this.launchSyncSubscription)) {
      this.launchSyncSubscription.unsubscribe()
      this.launchSyncSubscription = null
    }

    // Clean up failback subscription to prevent markForCheck() on destroyed component
    if (subscriptionActive(this.launchFailbackSubscription)) {
      this.launchFailbackSubscription.unsubscribe()
      this.launchFailbackSubscription = null
    }

    if (subscriptionActive(this.resourcesRefreshTimeoutSubscription)) {
      this.resourcesRefreshTimeoutSubscription.unsubscribe()
      this.resourcesRefreshTimeoutSubscription = null
    }
    this.resourcesRefreshing = false
    if (subscriptionActive(this.recoveryDataRefreshTimeoutSubscription)) {
      this.recoveryDataRefreshTimeoutSubscription.unsubscribe()
      this.recoveryDataRefreshTimeoutSubscription = null
    }
    this.recoveryDataRefreshing = false
    this.restoreLaunching = false
    // App-switch reuses this component instance — clear the conclude-to-standby
    // bridge so a wind-down on the prior app can't disable buttons / pin polling
    // on the next one.
    this.resetConcludeToStandbyBridge()
    this.cancelTabTransitionTimeout()

    // Cancel extended issue refresh timer to prevent stale API calls after navigation
    this.application?.cancelExtendedIssueRefresh()
    this.application = null
    this.cachedRecoveryResources = []
    this.cachedBackupResources = []
    this.cachedFailbackResources = []

    // Reset derived caches
    this._cachedBackupIssues = []
    this._cachedRestoreIssues = []
    this._cachedFailbackIssues = []
    this._cachedHasIssues = false
    this._cachedTotalIssueCount = 0
    this._issuesEverSeen = false
    this._cachedHasRecoveryResources = false
    this._cachedHasBackupResources = false
    this._cachedResourcesAreQuarantined = false
    this._cachedHasFailbackResources = false
    this._cachedHasRestorePermission = false
  }

  get loadingApplication() {
    return !this.application
  }

  public setApplication(application: Application) {
    // A held conflict describes a phase that has now moved on, so it stops gating the controls.
    // Compared before the assignment, since `this.application` is about to become the new one.
    if (application && this.application && application.syncPhase !== this.application.syncPhase) {
      this.clearOverviewError()
    }
    this.application = application
    if (application) {
      // Clear optimistic restore flag once backend confirms the phase transition
      if (this.restoreLaunching && application.syncPhase !== SyncPhaseDto.STANDBY) {
        this.restoreLaunching = false
      }
      // Drop the post-conclude bridge only once the conclude restore has
      // actually round-tripped to completion. Requires lastRestoreRequest to
      // have advanced past the baseline captured at click time — without that
      // check, an early stale-replica DTO (lastRestoreRequest from before the
      // click) would satisfy `STANDBY && !currentlyRestoring` and clear the
      // bridge prematurely, flashing the buttons enabled.
      if (
        this._isConcludingToStandby &&
        application.syncPhase === SyncPhaseDto.STANDBY &&
        !application.currentlyRestoring &&
        (application.lastRestoreRequest?.getTime() ?? 0) > this._concludeBaselineRequest
      ) {
        this._isConcludingToStandby = false
        this._concludeBaselineRequest = 0
      }
      const scope = this.tabToScope(this.currentTab)
      if (scope) {
        application.setActiveScope(scope)
      }
      // Auto-switch away from Failback tab if failback is no longer available
      // (e.g., app concluded recovery and left FAILOVER phase).
      // Must also update the active scope — direct tab assignment bypasses switchTab(),
      // so the scope would remain 'failback' while showing the Recovery Environment tab.
      if (this.currentTab === ResourcesTab.FAILBACK && !this.showFailback) {
        this.currentTab = ResourcesTab.RECOVERY_ENVIRONMENT
        const newScope = this.tabToScope(ResourcesTab.RECOVERY_ENVIRONMENT)
        if (newScope) {
          application.setActiveScope(newScope)
        }
        // Sync URL fragment so back/forward and page refresh land on the correct tab.
        const fragment = ApplicationComponent.tabToFragment(ResourcesTab.RECOVERY_ENVIRONMENT)
        this.router.navigate([], { relativeTo: this.route, fragment, replaceUrl: true })
      }
      // Catch the "already on Recovery Env when FAILOVER engages" case — the next poll cycle
      // will pick up the failback scope once it's in the active set, so the FAILBACK button
      // becomes accurate without the user needing to click the Failback sub-tab first.
      this.primeFailbackScopeIfNeeded()
      this.refreshResourceCache()
      this.recomputeTabStatuses()
      this.recomputeCachedDerivedValues()
      // Clear the tab-transition loading state once the active tab's scope has loaded its
      // resources (even if the array is empty — that's a valid terminal state for apps
      // with no configured resources). Uses the scope-specific isScopeLoaded check to
      // match switchTab()'s clearing logic — isLoaded depends on _activeScopes which may
      // not yet reflect the current tab when loading a cached app from the provider.
      const activeScope = this.tabToScope(this.currentTab)
      const scopeReady = !activeScope || application.isScopeLoaded(activeScope)
      if (this.tabTransitionLoading && scopeReady) {
        setTimeout(() => {
          this.tabTransitionLoading = false
          this.cancelTabTransitionTimeout()
          this.ref.markForCheck()
        }, 0)
      }
    }
    // OnPush: data-arrival path. setApplication is the entry point for every poll cycle
    // (loadApplication$ subscribe, refresh$ subscribes, setApplicationSyncPhase, failback,
    // concludeTestFailover, etc.) so marking here covers all of them.
    this.ref.markForCheck()
  }

  /** Refresh cached resource arrays for the active tab. Called on every setApplication()
   *  so that pill statuses stay in sync with the top-level tab status counts.
   *  Never replaces a non-empty cache with an empty array — prevents empty-state flash
   *  during transient refresh gaps where resources haven't loaded yet.
   *  Note: filterText is NOT passed here — the child component handles all text filtering
   *  client-side via PrimeNG. Passing filterText caused redundant re-renders on every
   *  keystroke (parent rebuilt a pre-filtered array → new [resources] reference → full
   *  ngOnChanges cycle in the child). */
  private refreshResourceCache(): void {
    if (!this.application) return
    let next: any[]
    switch (this.currentTab) {
      case ResourcesTab.RECOVERY_ENVIRONMENT:
        next = this.application.getRestoreResources()
        if (!this.resourcesEqual(next, this.cachedRecoveryResources)) {
          this.cachedRecoveryResources = next
        }
        break
      case ResourcesTab.BACKUP:
        next = this.application.getBackupResources()
        if (!this.resourcesEqual(next, this.cachedBackupResources)) {
          this.cachedBackupResources = next
        }
        break
      case ResourcesTab.FAILBACK:
        next = this.getFailbackResources()
        if (!this.resourcesEqual(next, this.cachedFailbackResources)) {
          this.cachedFailbackResources = next
        }
        break
    }
  }

  // Polling allocates a fresh ApplicationResource[] every cycle even when nothing changed
  // (service.ts builds new instances each tick). Without this guard, a no-op poll cycles a
  // new array reference into [resources], firing the child's ngOnChanges and re-running the
  // full filter pipeline on every poll. Compare the structurally-relevant fields; ignore
  // identity. O(n) but cheap relative to the alternative.
  private resourcesEqual(a: any[], b: any[]): boolean {
    if (a === b) return true
    if (!a || !b || a.length !== b.length) return false
    for (let i = 0; i < a.length; i++) {
      const x = a[i]
      const y = b[i]
      if (x.resourceArn !== y.resourceArn) return false
      if (x.status !== y.status) return false
      if (x.substatus !== y.substatus) return false
      if (x.progress !== y.progress) return false
      if (x.hasIssues !== y.hasIssues) return false
    }
    return true
  }

  private recomputeTabStatuses() {
    // Guard against null application — error handlers for async API calls (launchBackup,
    // launchRestore, etc.) can fire after the component is destroyed or navigated away from.
    if (!this.application) {
      console.error('recomputeTabStatuses() called with null application — caller fired after component cleanup')
      return
    }
    const latestRpTimestamp = this.computeLatestRpTimestamp()
    const appliedRpTimestamp = this.targetEnvironmentRecoveryPointTime

    // After resume/retry, preserve the optimistic "Initiating restore..." status until the restore
    // genuinely begins moving. The backend sets lastRequest synchronously, so currentlyRestoring flips
    // true on the first API response while the cached resources are still in their prior FAILED state —
    // which trips the Early Terminal Override and snaps the headline back to the failure. Hold the
    // optimistic text until a resource actually enters PENDING/PROCESSING (or the rapid-refresh expires).
    let skipRestoreCompute = false
    if (this._expectingRestoreToStart) {
      const expired = !this.rapidRefreshExpires || new Date() >= this.rapidRefreshExpires
      const restoreMoving = this.application
        .getRestoreResources()
        .some((r) => r.status === ResourceStatus.PENDING || r.status === ResourceStatus.PROCESSING)
      if (restoreMoving || expired) {
        this._expectingRestoreToStart = false // Resources are moving or safety timeout — let real status take over
      } else {
        skipRestoreCompute = true
      }
    }

    if (!skipRestoreCompute) {
      if (this.application.isScopeLoaded('restore')) {
        const syncStatus = computeProcessStatus(
          this.application.lastRestoreRequest,
          this.application.lastRestoreStart,
          this.application.lastRestoreSuccess,
          this.application.lastRestoreFailure
        )
        const recoveryResources = this.application.getRestoreResources()
        // Only show "no data" if there are truly no resources AND we're not waiting for initial backup.
        // During initial backup creation, restore resources don't exist yet — let computeRecoveryEnvStatus
        // handle this case so it can show backup-aware status ("Your first backup is in progress...").
        const isAwaitingInitialBackup =
          !this.application.lastBackupSuccess &&
          (this.application.currentlyBackingUp || !!this.application.lastBackupRequest)
        // Only assert "added after recovery started" once a restore has actually completed in
        // this cycle. Before then, the empty list is just early state and computeRecoveryEnvStatus
        // owns the right copy ("Preparing to restore...").
        const isInRecovery =
          this.application.syncPhase === SyncPhaseDto.FAILOVER ||
          this.application.syncPhase === SyncPhaseDto.FAILOVER_TEST
        // A first standby restore that's held by a sibling recovery has no restore resources yet,
        // so without this the empty-resource branch below would swallow it with the generic "no
        // resources" copy. Let it fall through to computeRecoveryEnvStatus, which renders the
        // blocked-by-sibling message.
        const blockedBySibling = this.computeStandbyRestoreBlockedBySibling()
        if (
          recoveryResources.length === 0 &&
          !this.appIsDeleted &&
          !isAwaitingInitialBackup &&
          !this.application.currentlyRestoring &&
          !blockedBySibling &&
          (!isInRecovery || !!this.application.lastRestoreSuccess)
        ) {
          this._recoveryEnvStatus = {
            state: AppStatusState.INFO,
            text: isInRecovery
              ? "Resources added after recovery started aren't part of the applied recovery point."
              : 'No recovery resources available yet. Once your first backup completes, a standby restore will begin automatically.',
            issues: [],
          }
        } else {
          const failedRestoreCount = recoveryResources.filter((r) => r.status === ResourceStatus.FAILED).length
          // Pilot-light resources are computed as SUCCEEDED in resource-viewer-base
          // (see isPilotLight), so they roll up into this count without special casing.
          const successCount = recoveryResources.filter((r) => r.status === ResourceStatus.SUCCEEDED).length
          // Count resources still actively being processed (not yet terminal)
          const inProgressCount = recoveryResources.filter(
            (r) => r.status === ResourceStatus.PENDING || r.status === ResourceStatus.PROCESSING
          ).length
          this._recoveryEnvStatus = computeRecoveryEnvStatus({
            syncPhase: this.application.syncPhase,
            syncStatus,
            appliedRpTimestamp: appliedRpTimestamp ?? latestRpTimestamp,
            failedRestoreCount,
            restoreIssues: this.application.restoreIssues || [],
            appIsDeleted: this.appIsDeleted,
            allResourcesCleaned: this.allResourcesCleaned,
            successCount,
            totalCount: recoveryResources.length,
            lastRestoreSuccess: this.application.lastRestoreSuccess,
            backupContext: {
              isBackingUp: this.application.currentlyBackingUp,
              hasEverSucceeded: !!this.application.lastBackupSuccess,
            },
            inProgressCount,
            standbyRestoreBlockedBySibling: blockedBySibling,
          })
        }
      } else {
        this._recoveryEnvStatus = null
      }
    }

    // Clear the concluding-test flag once the restore finishes (safety fallback in case
    // the subscription finalize doesn't fire, e.g. data arrives via a different refresh path).
    if (this._concludingTest && !this.isStandbyRestoreConcluding) {
      this._concludingTest = false
    }

    // If we're expecting a backup to start (after editor save), preserve the optimistic
    // "Planning backup..." until the backend shows backup activity. Scope-local flag so
    // only the backup block is skipped — recovery and failback still recompute.
    let skipBackupCompute = false
    if (this._expectingBackupToStart) {
      const expired = !this.rapidRefreshExpires || new Date() >= this.rapidRefreshExpires
      if (this.application.currentlyBackingUp || expired) {
        this._expectingBackupToStart = false // Backend caught up or safety timeout — let real status take over
      } else {
        skipBackupCompute = true
      }
    }

    if (!skipBackupCompute) {
      if (this.application.isScopeLoaded('backup')) {
        const stagingStatus = computeProcessStatus(
          this.application.lastBackupRequest,
          this.application.lastBackupStart,
          this.application.lastBackupSuccess,
          this.application.lastBackupFailure
        )
        const backupResources = this.application.getBackupResources()
        if (backupResources.length === 0) {
          this._backupHealthStatus = {
            state: AppStatusState.INFO,
            text: 'No data available yet. Configure your resources to create initial backup.',
            issues: [],
          }
        } else {
          const failedBackupCount = backupResources.filter((r) => r.status === ResourceStatus.FAILED).length
          const backupSuccessCount = backupResources.filter((r) => r.status === ResourceStatus.SUCCEEDED).length
          const hasQueuedBackupResources = backupResources.some((r) => r.status === ResourceStatus.PENDING)
          // While backing up, the latest RP is the freshly minted in-progress one — using it
          // would make priorBackupInfo read "Last successful backup: just now" while resources
          // are still queued. Use the prior cycle's RP, or null when none can be found so
          // computeBackupHealthStatus simply hides the subtext.
          // While a navigation refresh is in flight the cached recovery points are stale, so
          // pass null to hide the "Last successful backup" subtext rather than show a wrong time.
          const backupRpTimestamp = this.recoveryDataRefreshing
            ? null
            : this.application.currentlyBackingUp
              ? this.computePriorBackupRpTimestamp()
              : latestRpTimestamp
          this._backupHealthStatus = computeBackupHealthStatus({
            syncPhase: this.application.syncPhase,
            stagingStatus,
            rpTimestamp: backupRpTimestamp,
            failedBackupCount,
            backupIssues: this.application.backupIssues || [],
            scanIssues: this.application.scanIssues || [],
            isRpoViolation: false, // RPO violation detection — to be wired up
            isBackupActive: this.application.currentlyBackingUp,
            successCount: backupSuccessCount,
            totalCount: backupResources.length,
            lastBackupStart: this.application.lastBackupStart,
            lastBackupSuccess: this.application.lastBackupSuccess,
            lastBackupFailure: this.application.lastBackupFailure,
            hasQueuedResources: hasQueuedBackupResources,
          })
        }
      } else {
        this._backupHealthStatus = null
      }
    }

    // Failback status — only computed when the failback sub-tab is available and app is not deleted
    // (deleted apps show recovery cleanup status instead, failback is not relevant)
    if (this.showFailback && !this.appIsDeleted && this.application.isScopeLoaded('failback')) {
      const failbackProcessStatus = computeProcessStatus(
        this.application.lastFailbackRequest,
        this.application.lastFailbackStart,
        this.application.lastFailbackSuccess,
        this.application.lastFailbackFailure
      )
      // Use unfiltered resources for status computation (not affected by search text)
      const failbackResources = this.application
        .getFailbackResources()
        .filter((res) => res.resourceTypeDetails.supportsFailback)
      const failedFailbackCount = failbackResources.filter((r) => r.status === ResourceStatus.FAILED).length
      const failbackSuccessCount = failbackResources.filter((r) => r.status === ResourceStatus.SUCCEEDED).length

      // Count resources still actively being processed (not yet terminal)
      const failbackInProgressCount = failbackResources.filter(
        (r) => r.status === ResourceStatus.PENDING || r.status === ResourceStatus.PROCESSING
      ).length
      this._failbackStatus = computeFailbackStatus({
        processStatus: failbackProcessStatus,
        failedCount: failedFailbackCount,
        issues: this.application.failbackIssues || [],
        successCount: failbackSuccessCount,
        totalCount: failbackResources.length,
        inProgressCount: failbackInProgressCount,
      })
    } else {
      this._failbackStatus = null
    }
  }

  /** Recompute all cached derived values that the template reads via getters.
   *  Called once from setApplication() so these don't recalculate on every CD cycle. */
  private recomputeCachedDerivedValues(): void {
    // Union of every resource DTO seen across scopes — necessary because inactive scopes'
    // arrays are nulled by the polling cycle. Without this persistent lookup, any issue whose
    // scope isn't currently active would lose its resourceType/resourceName enrichment.
    const allResourceDtos = this.application?.allKnownResourceDtos || []
    this._cachedBackupIssues = makeViewableIssues(this.application?.recoveryPointIssues || [], allResourceDtos)
    this._cachedRestoreIssues = makeViewableIssues(this.application?.restoreIssues || [], allResourceDtos)
    this._cachedFailbackIssues = makeViewableIssues(this.application?.failbackIssues || [], allResourceDtos)
    const currentIssues = this.application?.allIssues || []
    // Issues are confirmed empty when the API has responded and returned no issues.
    // This distinguishes "no issues" from "issues not yet loaded" (issuesLoaded is false
    // before the first API response).
    const issuesConfirmedEmpty = currentIssues.length === 0 && this.application?.issuesLoaded
    // Sticky: keep hasIssues true while user is on Issues tab to prevent
    // tab disappearing during transient empty polls from issue refresh.
    // Clear sticky once issues are confirmed loaded and empty (genuinely resolved).
    const stickyIssues = this.currentTab === ResourcesTab.ISSUES && this._cachedHasIssues && !issuesConfirmedEmpty
    this._cachedHasIssues = currentIssues.length > 0 || stickyIssues
    this._cachedTotalIssueCount = currentIssues.length
    // Track whether issues have ever been seen for this application instance.
    // Used to prevent auto-redirect away from the Issues tab during transient
    // empty polls — if issues existed, the user explicitly chose this tab.
    if (currentIssues.length > 0) {
      this._issuesEverSeen = true
    }
    // Only redirect away from Issues tab when navigating TO an app that has never
    // had issues (e.g. switching from an app with issues while on the Issues tab).
    // Never redirect when issues were previously seen — transient empty polls from
    // the extended issue refresh cycle can cause a false "no issues" state that
    // would kick the user off the tab and trigger a disruptive full-page reload.
    if (
      this.currentTab === ResourcesTab.ISSUES &&
      issuesConfirmedEmpty &&
      !this._cachedHasIssues &&
      !this._issuesEverSeen
    ) {
      this.showBackupsTab()
    }
    this._cachedHasRecoveryResources = this.application.getRestoreResources().length > 0
    this._cachedHasBackupResources = this.application.getBackupResources().length > 0
    this._cachedResourcesAreQuarantined =
      this.appResourcesLoaded && this.accountService.quarantineEnabled && this.getQuarantinedResources().length > 0
    // Don't pass filterText — failback availability is app-level state, not filter-dependent.
    this._cachedHasFailbackResources = this.appResourcesLoaded && this.getFailbackResources().length > 0
    this._cachedHasRestorePermission = this.getRestorePermission()
    this._cachedLastBackupStatus = new ProcessingStatus('backup', this.application.backupStatus)
    this._cachedLastRestoreStatus = new ProcessingStatus('restore', this.application.restoreStatus)
    this._cachedLastFailback = new ProcessingStatus('failback', this.application.lastFailback)
  }

  private computeLatestRpTimestamp(): Date {
    // Use the latest recovery point from the array when available — this includes
    // newly created RPs from a backup that hasn't been restored yet, so the backup
    // tab shows the correct "Last successful backup" timestamp immediately.
    if (this.application.recoveryPoints?.length > 0) {
      return this.application.recoveryPoints.reduce(
        (accum, curr) => (!!accum ? (accum.timestamp > curr.timestamp ? accum : curr) : curr),
        null
      )?.timestamp
    }
    // Multi-account: if RPs not loaded yet, no timestamp available
    if (this.accountService.entitledForCapability('appGroupRestore')) {
      return null
    }
    // Single-account fallback when recovery points haven't been fetched yet
    return this.targetEnvironmentRecoveryPointTime
  }

  // Newest RP whose timestamp is at or before lastBackupSuccess — i.e., the RP from the
  // prior completed backup cycle, excluding the in-progress RP that was minted when the
  // current backup started. Used so the "Last successful backup: X ago" subtext reflects
  // the actual prior success, not the in-flight one.
  private computePriorBackupRpTimestamp(): Date | null {
    const lastSuccess = this.application.lastBackupSuccess
    if (!lastSuccess || !this.application.recoveryPoints?.length) return null
    const priorRp = this.application.recoveryPoints
      .filter((rp) => rp.timestamp <= lastSuccess)
      .reduce((accum, curr) => (!accum || curr.timestamp > accum.timestamp ? curr : accum), null)
    return priorRp?.timestamp ?? null
  }

  private get applicationRefreshInterval() {
    if (!!this.application && this.application.currentlyActive) {
      return ApplicationComponent.RAPID_REFRESH_INTERVAL
    }
    // The conclude-to-standby wind-down disables the test/recovery buttons. Poll
    // at the active cadence the whole time it holds (the optimistic bridge can
    // outlast currentlyRestoring) so the buttons re-enable within ~2s of the
    // restore resolving instead of waiting for the slow background interval.
    if (this.isConcludingToStandby) {
      return ApplicationComponent.RAPID_REFRESH_INTERVAL
    }
    // Use rapid refresh when application is marked as deleted but not fully deleted yet
    if (!!this.application && this.appIsDeleted && !this.hasCompletedDeletionProcess(this.application)) {
      return ApplicationComponent.RAPID_REFRESH_INTERVAL
    }
    if (new Date() < this.rapidRefreshExpires) {
      return ApplicationComponent.RAPID_REFRESH_INTERVAL
    }
    // Keep rapid polling when backup just completed but first restore hasn't started yet,
    // so the recovery tab updates promptly instead of waiting up to 60s
    if (
      this.application &&
      this.application.lastBackupSuccess &&
      !this.application.lastRestoreSuccess &&
      !this.application.currentlyRestoring
    ) {
      return ApplicationComponent.RAPID_REFRESH_INTERVAL
    }
    return ApplicationComponent.APPLICATION_REFRESH_INTERVAL
  }

  private initializeApplication(accountId: string, applicationId: string): void {
    /**
     * Load the application object.  This is called every time the URL changes to a new
     * application.
     */

    // shut-down any in-progress data loads, refresh timers, etc.
    this.cleanup()

    // Safety net for the rare case where the poll loop's two subscriptions both die (the
    // "timers lost" state). Recover in place by re-arming the refresh timer rather than a
    // jarring full-page reload. Only handle nothingActive: allActive is a benign transient
    // race that self-corrects on the next cycle, and tearing down on it could clobber an
    // in-flight restore/failback operation (those also run on applicationSubscription).
    this.timerIntegrityCheckSubscription = interval(10000).subscribe((_) => {
      if (this.nothingActive && this.application) {
        console.warn('Application poll loop lost; rescheduling refresh.')
        this.rescheduleApplicationRefresh()
      }
    })

    this.setApplication(null)
    // A cached application emits synchronously, so the @if never collapses and the host is not
    // destroyed — without this the previous application's conflict renders on the next one.
    this.clearOverviewError()
    this.tabTransitionLoading = true
    // Safety net: clear tabTransitionLoading after 5 seconds to prevent permanent blank screens
    // if the initial resource load fails or takes unexpectedly long
    this.cancelTabTransitionTimeout()
    this.tabTransitionTimeoutSubscription = timer(5000).subscribe(() => {
      if (this.tabTransitionLoading) {
        this.tabTransitionLoading = false
        this.ref.markForCheck()
      }
      this.tabTransitionTimeoutSubscription = null
    })
    const fragment = this.route.snapshot.fragment
    const routeTab = this.route.snapshot.data?.tab
    const fragmentTab = fragment ? ApplicationComponent.fragmentToTab(fragment) : null
    if (fragmentTab != null) {
      this.currentTab = fragmentTab
    } else if (this.pendingTab != null) {
      this.currentTab = this.pendingTab
    } else if (routeTab === 'issues') {
      this.currentTab = ResourcesTab.ISSUES
    } else if (this.currentTab == null) {
      this.currentTab = ResourcesTab.BACKUP
    }
    // else: retain the current tab when switching between applications
    this.pendingTab = null
    // Keep the URL fragment in sync with the active tab
    const tabFragment = ApplicationComponent.tabToFragment(this.currentTab)
    if (tabFragment) {
      this.router.navigate([], { relativeTo: this.route, fragment: tabFragment, replaceUrl: true })
    }

    // If we're navigating here from another page (like the application editor page) that may have kicked off
    // some activity on this application, we might want to refresh immediately.  But there's a race condition
    // where an immediate refresh might not pick up activity that was kicked off on the prior page.
    // This rapidRefresh setting indicates that we should refresh frequently for the first bit of time so
    // that we will pick up that activity.  It's turned on via window history from the prior page.
    if (!!window.history.state) {
      this.rapidRefreshExpires = window.history.state.rapidRefreshExpires || null
      this.delayRapidRefresh = !!window.history.state.delayRapidRefresh
      this.forceRefreshResources = !!window.history.state.forceRefreshResources

      // After editor save, show optimistic backup status to bridge the latency gap
      // between save and the backend starting the backup. The _expectingBackupToStart
      // flag prevents recomputeTabStatuses() from overwriting this until the backend
      // reports actual backup activity.
      if (window.history.state.expectBackupAfterSave) {
        this._backupHealthStatus = {
          state: AppStatusState.INDETERMINATE,
          text: 'Planning backup...',
          issues: [],
        }
        this._expectingBackupToStart = true
      }
    }

    // When navigating from the editor with forceRefreshResources, show a loading spinner
    // in the resources table until fresh data arrives — UNLESS optimistic resources were
    // already injected by the editor, in which case the data is ready to display immediately.
    // Check the cached Application (not this.application which is null after cleanup)
    const cachedApp = this.applicationProviderService.getCachedApplication(applicationId)
    if (this.forceRefreshResources && !cachedApp?.hasOptimisticResources) {
      this.resourcesRefreshing = true
      this.resourcesRefreshTimeoutSubscription = timer(5000).subscribe(() => {
        this.resourcesRefreshing = false
        this.resourcesRefreshTimeoutSubscription = null
        this.ref.markForCheck()
      })
    }

    // A cached, loaded app emits its previous-visit recovery data synchronously (concat's
    // of(application)) before the navigation refresh fetches fresh data. Show a loading state
    // for the recovery-derived UI (RPO chart + backup timestamp) until that refresh lands so
    // we never flash a stale value. Fresh/never-loaded apps have null recovery data and rely
    // on the existing recoveryPointAttemptsLoaded gate, so the flag is left false for them.
    if (cachedApp?.isLoaded) {
      this.recoveryDataRefreshing = true
      this.recoveryDataRefreshTimeoutSubscription = timer(5000).subscribe(() => {
        this.recoveryDataRefreshing = false
        this.recoveryDataRefreshTimeoutSubscription = null
        this.ref.markForCheck()
      })
    }

    // Track emissions to know when we've received fresh data after the initial cached emission
    let emissionCount = 0

    this.applicationSubscription = this.applicationProviderService
      .loadApplication$(applicationId, accountId, !this.delayRapidRefresh, this.forceRefreshResources)
      .pipe(
        takeUntil(this.destroy$),
        switchMap((application) => {
          // Check if deleted and resources is empty or falsy to prevent unnecessary api calls
          if (application?.dto?.deleted && !application.hasResources) {
            // Use ApiService to get the list of applications and check if the current application is in that list
            return this.apiService.listApplications(accountId).pipe(
              first(),
              switchMap((appDtos) => {
                const exists = appDtos.some((appDto: any) => appDto.appId === application.applicationId)
                if (!exists) {
                  this.router.navigate([`/${accountId}/deleted`])
                  // Complete the observable chain
                  return of(null)
                }
                // If exists, continue with the application
                return of(application)
              }),
              catchError((_e) => {
                this.router.navigate([`/${accountId}/deleted`])
                return of(null)
              })
            )
          }
          return of(application)
        }),
        tap((application) => this.pulseSpinnerIfActive(application)),
        finalize(() => {
          this.applicationSubscription = null
          this.isPolling = false
          this.rescheduleApplicationRefresh()
        })
      )
      .subscribe(
        (application) => {
          emissionCount++
          // Drop emissions whose app no longer matches the route — a stale subscription whose
          // teardown didn't propagate before the next emission would otherwise overwrite the
          // currently-displayed app's reference.
          if (application && application.applicationId !== this.applicationId) {
            return
          }
          if (application) {
            // Clear before setApplication: recomputeTabStatuses() bakes the backup-timestamp
            // subtext based on this flag, so it must be false before that runs on the fresh
            // emission or the subtext would stay hidden until the next poll. The flag is only
            // set when there's a cached emission (emission 1), so emission 2 is the navigation
            // refresh carrying fresh recovery data.
            if (this.recoveryDataRefreshing && emissionCount >= 2) {
              this.clearRecoveryDataRefreshSpinner()
            }

            this.setApplication(application)

            // When forceRefreshResources is true, keep the resources spinner until we've
            // received emissions that should contain fresh resource data.
            // We clear on emission 2 and rely on subsequent emissions to update if needed.
            if (this.resourcesRefreshing && emissionCount >= 2) {
              this.clearResourcesRefreshSpinner()
            }
          }
        },
        handleArpioError(window.location.href, this.apiService, this.uiService)
      )

    // Only accept broadcasts with loaded resources. This ignores the light background
    // loop DTOs and waits for the heavy overview instance, preventing UI tearing
    // by ensuring buttons, headers, and charts all update together.
    Application.publishSubject$
      .pipe(
        filter(
          (event: CrudEvent<Application>) =>
            event.action === CrudAction.UPSERT && event.entityId === this.applicationId && !!event.entity
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((event: CrudEvent<Application>) => {
        const entity = event.entity!
        const activeScope = this.tabToScope(this.currentTab)
        if (!activeScope || entity.isScopeLoaded(activeScope)) {
          // Resources for the active tab are present — adopt this instance and recompute together.
          this.setApplication(entity)
        } else if (this.application && entity.syncPhase !== this.application.syncPhase) {
          // Phase change detected. Kick our local heavy poll immediately to fetch resources
          // and update the UI atomically, avoiding the multi-second delay.
          // Edge-triggered to prevent a polling loop.
          this.application.forceImmediatePoll()
        }
      })
  }

  private clearResourcesRefreshSpinner(): void {
    if (subscriptionActive(this.resourcesRefreshTimeoutSubscription)) {
      this.resourcesRefreshTimeoutSubscription.unsubscribe()
      this.resourcesRefreshTimeoutSubscription = null
    }
    this.resourcesRefreshing = false
    this.ref.markForCheck()
  }

  private clearRecoveryDataRefreshSpinner(): void {
    if (subscriptionActive(this.recoveryDataRefreshTimeoutSubscription)) {
      this.recoveryDataRefreshTimeoutSubscription.unsubscribe()
      this.recoveryDataRefreshTimeoutSubscription = null
    }
    this.recoveryDataRefreshing = false
    this.ref.markForCheck()
  }

  private refreshApplication(): void {
    // NOTE: If the app is deleted and the backend returns 404, the user stays on this page
    // with an error toast. We currently have no landing page to redirect to (no application
    // list page or recovery group overview page exists yet — both are backlogged features).
    // For now we leave navigation in the user's hands. Reassess when either feature ships.
    //
    // Cancel both subscriptions to prevent the finalize() from creating a competing
    // timer via rescheduleApplicationRefresh() when we're about to start a new subscription.
    this.cancelSubscriptionsInCorrectOrder()

    // No app to poll (mid-cleanup / route change): re-arm the timer instead of dereferencing a
    // null application below, which would throw and leave both subscriptions dead ("timers lost").
    if (!this.application) {
      this.rescheduleApplicationRefresh()
      return
    }

    // Show spinner during active operations
    if (this.application.currentlyActive) {
      this.isPolling = true
    }

    try {
      this.applicationSubscription = this.application
        .refresh$(true)
        .pipe(
          takeUntil(this.destroy$),
          tap((application) => this.pulseSpinnerIfActive(application)),
          finalize(() => {
            this.applicationSubscription = null
            this.isPolling = false
            this.rescheduleApplicationRefresh()
          })
        )
        .subscribe(
          (application) => {
            if (application && application.applicationId !== this.applicationId) {
              return
            }
            this.setApplication(application)
          },
          handleArpioError(window.location.href, this.apiService, this.uiService)
        )
    } catch (err) {
      // A synchronous failure while establishing the poll must not strand the loop with both
      // subscriptions dead. Re-arm the refresh timer so polling self-heals on the next tick.
      console.error('Failed to start application refresh; rescheduling.', err)
      this.applicationSubscription = null
      this.isPolling = false
      this.rescheduleApplicationRefresh()
    }
  }

  // A tab that regains focus while still "visible" (e.g. returning from another window) won't
  // trip the visibility-based re-poll in visibilityAwareTimer. Poke an immediate poll on focus
  // so the view converges to current server state instead of waiting out the poll interval.
  // forceImmediatePoll only nexts a Subject, so it cannot churn subscriptions or interrupt an
  // in-flight restore/failback.
  @HostListener('window:focus')
  onWindowFocus(): void {
    this.application?.forceImmediatePoll()
  }

  // Soft, in-place reload triggered by the "reload now" affordance — recovers a stuck or
  // stale-feeling view without the jarring white-flash of a full-page reload, and keeps the user
  // in the console. Dropping the cached resources flips isScopeLoaded false so the tables fall back
  // to skeleton rows, then a forced refresh repopulates them with fresh server data. Falls back to
  // a full reload only if there is no loaded application to refresh in place.
  reloadView(): void {
    if (!this.application) {
      window.location.reload()
      return
    }
    this.application.invalidateResourceCaches()
    this.forceFullRefresh()
    this.ref.markForCheck()
  }

  // Current auto-refresh cadence in seconds, for the "Auto-refreshes every Ns" indicator.
  get autoRefreshSeconds(): number {
    return Math.max(1, Math.round(this.applicationRefreshInterval / 1000))
  }

  rescheduleApplicationRefresh() {
    this.cancelApplicationRefreshTimerSubscription()
    // visibilityAwareTimer throttles to 60s when tab is hidden, and fires immediately on tab-return
    this.applicationRefreshTimerSubscription = visibilityAwareTimer(this.applicationRefreshInterval).subscribe((_) =>
      this.refreshApplication()
    )
  }

  /**
   * Shows a brief spinner pulse when the application is actively processing.
   * Call this on each poll emission to create a visual indicator of polling activity.
   */
  private pulseSpinnerIfActive(application: Application): void {
    if (application?.currentlyActive) {
      this.isPolling = true
      this.ref.detectChanges()
      // Hide spinner after a short delay to create a "pulse" effect
      setTimeout(() => {
        this.isPolling = false
        this.ref.detectChanges()
      }, 500)
    } else {
      this.isPolling = false
    }
  }

  // One-shot forced refresh of the application DTO + resources. Invoked by reloadView()'s
  // "refresh now" affordance; the schedule already refreshes on its own, but this lets the user
  // pull fresh data immediately. Guards against overlapping forced refreshes.
  forceFullRefresh(): void {
    if (!subscriptionActive(this.forceRefreshSubscription)) {
      this.manualRefreshing = true
      this.forceRefreshSubscription = this.application
        .refresh$(true, false, true)
        .pipe(
          takeUntil(this.destroy$),
          finalize(() => {
            this.forceRefreshSubscription = null
            this.manualRefreshing = false
            this.ref.markForCheck() // OnPush: spinner state change after refresh completes
          })
        )
        .subscribe(
          (application) => {
            if (application && application.applicationId !== this.applicationId) {
              return
            }
            this.setApplication(application)
          },
          handleArpioError(window.location.href, this.apiService, this.uiService)
        )
    }
  }

  get refreshing() {
    return (
      subscriptionActive(this.forceRefreshSubscription) ||
      (subscriptionActive(this.applicationSubscription) && this.appIsDeleted) ||
      this.isPolling
    )
  }

  getFailbackResources(filterText?: string): Array<FailbackResource> {
    return this.application.getFailbackResources(filterText).filter((res) => res.resourceTypeDetails.supportsFailback)
  }

  getQuarantinedResources(): Array<ApplicationResource> {
    // Don't pass filterText — quarantine state is app-level, not filter-dependent.
    return this.application.getRestoreResources().filter((res) => isStatusQuarantineType(res.status))
  }

  // Navigate to Issues tab and scroll/highlight the issues associated with a clicked resource
  onResourceIssuesClicked(resource: ViewableApplicationResource): void {
    if (!resource.issues || resource.issues.size === 0) return
    this.showIssuesTab()
    const issueIds = Array.from(resource.issues)
      .map((i) => i.issueId)
      .filter(Boolean)
    if (issueIds.length > 0) {
      this.pendingScrollToIssueId = issueIds[0]
      this.pendingHighlightIssueIds = issueIds
      this.hasScrolledToIssue = false
    }
  }

  getBackupResources(): ApplicationResource[] {
    return this.application.getBackupResources(this.filterText)
  }

  getRestoreResources(): ApplicationResource[] {
    return this.application.getRestoreResources(this.filterText)
  }

  get hasRecoveryResources(): boolean {
    return this._cachedHasRecoveryResources
  }

  get hasBackupResources(): boolean {
    return this._cachedHasBackupResources
  }

  get lastStagingRequest(): Date {
    return this.application.lastBackupRequest
  }

  get lastStagingStart(): Date {
    return this.application.lastBackupStart
  }

  get lastStagingSuccess(): Date {
    return this.application.lastBackupSuccess
  }

  get lastStagingFailure(): Date {
    return this.application.lastBackupFailure
  }

  get lastSyncRequest(): Date {
    return this.application.lastRestoreRequest
  }

  get lastSyncStart(): Date {
    return this.application.lastRestoreStart
  }

  get lastSyncSuccess(): Date {
    return this.application.lastRestoreSuccess
  }

  get lastSyncFailure(): Date {
    return this.application.lastRestoreFailure
  }

  get targetEnvironmentRecoveryPointTime() {
    const currentRp = this.application.currentRecoveryPoint
    return currentRp ? currentRp.timestamp : null
  }

  public recoverButtonClick() {
    // Reset confirmation state so a prior cancelled run can't leak us back
    // into the "Are you sure?" screen with stale selections.
    this.showAreYouSure = false
    this.pendingRecoverySettings = null
    this.failoverDialog.open()
  }

  // Pre-flight for sync-phase transitions: optimistic status text + 30s rapid (2s)
  // polling to cover async step-function startup latency. Pair with
  // recomputeTabStatuses() on the error path to roll back on failure.
  private setupOptimisticTransition(statusText: string) {
    // The previous attempt's conflict is superseded by the one the user just started.
    this.clearOverviewError()
    this._recoveryEnvStatus = {
      state: AppStatusState.INDETERMINATE,
      text: statusText,
      issues: [],
    }
    // Hold the optimistic text until a resource actually moves, the same way the resume/retry
    // path does. Without it the first recompute reports the previous restore's terminal state.
    this._expectingRestoreToStart = true
    this.rapidRefreshExpires = new Date(Date.now() + 30000)
  }

  // Rollback partner for setupOptimisticTransition — releases the hold so the recompute
  // can report the real status instead of leaving the request looking in flight.
  private revertOptimisticTransition() {
    this._expectingRestoreToStart = false
    this.recomputeTabStatuses()
  }

  private setApplicationSyncPhase(restoreSettings: Array<LaunchRestoreSettings>, syncPhase: SyncPhaseDto) {
    /*
      Invoke the server API to launch a sync/restore.
      This method launches the restore, and then returns an observable that will emit once
      when the restore has successfully begun.  We do some rxjs trickery to make one API
      call emit to multiple subscribers here (see the comments below).
    */

    // Cancel the applicaiton subscription and refresh subscription.
    this.cancelSubscriptionsInCorrectOrder()

    // Create an observable that can be shared across multiple subscriptions.  We do this
    // with the shareReplay operator, specifying a buffer size of 1 so that late subscriptions
    // will always get the most recently emitted value if one exists.  We also use the
    // refCount flag so that when all subscribers have stopped listening, the observable
    // will stop emitting.
    const updateObservable = Application.updateApplicationSyncPhases$(
      this.application,
      restoreSettings,
      syncPhase
    ).pipe(
      tap(() =>
        restoreSettings.forEach((rs) =>
          this.crossTabSync.notifyApplicationChanged(
            this.accountService.accountId,
            rs.application.applicationId,
            syncPhase
          )
        )
      ),
      shareReplay({ bufferSize: 1, refCount: true })
    )

    // Now, subscribe to that observable for the first time.  This is the subscription that will
    // poll until the restore has completed.
    this.applicationSubscription = updateObservable
      .pipe(
        takeUntil(this.destroy$),
        switchMap((_) => this.application.refresh$(false)), // refreshDto=false on first poll: preserve the fresh DTO from initiateRestore to avoid a race where getApplication() returns stale data before the DB write completes
        tap((application) => this.pulseSpinnerIfActive(application)),
        catchError((error) => (error instanceof Http409Error ? this.application.refresh$(true) : throwError(error))),
        finalize(() => {
          this.rescheduleApplicationRefresh()
          this.applicationSubscription = null
          this.isPolling = false
        })
      )
      .subscribe(
        (application) => {
          if (application && application.applicationId !== this.applicationId) {
            return
          }
          this.setApplication(application)
        },
        handleArpioError(window.location.href, this.apiService, this.uiService)
      )

    // Finally, return an observable that will emit once when the update API call completes.
    // The caller can subscribe to this and track the subscription state (with subscriptionActive())
    // to know when the call was successful.
    return updateObservable.pipe(first())
  }

  onLaunchRecovery(settings: LaunchRestoreSettings[]) {
    this.selectedApplications = settings.map((s) => s.application)
    this.quarantineEnabled = settings[0]?.quarantineEnabled
    this.pendingRecoverySettings = settings
    this.showAreYouSure = true
  }

  onCancelRecoveryConfirmation() {
    this.showAreYouSure = false
    this.pendingRecoverySettings = null
    this.failoverDialog.close()
  }

  // launch-recovery-dialog owns its own Escape listener, but it unmounts when
  // showAreYouSure flips to true. Own the Escape path here so the confirm step
  // stays dismissable.
  @HostListener('document:keydown.escape', ['$event'])
  onConfirmationEscape(event: KeyboardEvent): void {
    if (!this.showAreYouSure) return
    event.preventDefault()
    event.stopPropagation()
    this.onCancelRecoveryConfirmation()
  }

  confirmLaunchRecovery() {
    if (this.pendingRecoverySettings) {
      this.launchRecovery(this.pendingRecoverySettings)
      this.pendingRecoverySettings = null
      this.showAreYouSure = false
    }
  }

  launchRecovery(restoreSettings: LaunchRestoreSettings[]) {
    /**
     * Places multiple applications into the failover state and then launches
     * a sync on the sync pair.
     */

    this.setupOptimisticTransition('Initiating recovery...')
    this.launchSyncSubscription = this.setApplicationSyncPhase(restoreSettings, SyncPhaseDto.FAILOVER)
      .pipe(finalize(() => (this.launchSyncSubscription = null)))
      .subscribe(
        (_) => {
          this.restoreLaunching = true
          this.showRecoveryEnvironmentTab()
          this.ref.markForCheck()
          this.failoverDialog.close()
        },
        (error) => {
          this.revertOptimisticTransition() // Revert default optimistic status on failure
          handleArpioError(window.location.href, this.apiService, this.uiService, {
            errorContext: { objectId: 'application-overview' },
          })(error)
        }
      )
  }

  initiateFailback(applications: Application[]) {
    /*
      Same kind of logic as setApplicationSyncPhase, but this time for launching a failback.
    */

    // Create an observable that can be shared across multiple subscriptions.  We do this
    // with the shareReplay operator, specifying a buffer size of 1 so that late subscriptions
    // will always get the most recently emitted value if one exists.  We also use the
    // refCount flag so that when all subscribers have stopped listening, the observable
    // will stop emitting.
    const updateObservable = Application.initiateFailback$(this.application, applications).pipe(
      shareReplay({ bufferSize: 1, refCount: true })
    )

    // Now, subscribe to that observable the first time.  This is the subscription that will
    // poll until the failback has completed.
    this.applicationSubscription = updateObservable
      .pipe(
        takeUntil(this.destroy$),
        tap((application) => this.pulseSpinnerIfActive(application)),
        finalize(() => {
          this.rescheduleApplicationRefresh()
          this.applicationSubscription = null
          this.isPolling = false
        })
      )
      .subscribe(
        (application) => {
          if (application && application.applicationId !== this.applicationId) {
            return
          }
          this.setApplication(application)
        },
        handleArpioError(window.location.href, this.apiService, this.uiService)
      )

    // Finally, return an observable that will emit once when the update API call completes.
    // The caller can subscribe to this and track the subscription state (with subscriptionActive())
    // to know when the call was successful.
    return updateObservable.pipe(first())
  }

  launchFailback(launchEvent: FailbackSettings) {
    // Cancel any current refresh, or refresh timer.
    this.cancelSubscriptionsInCorrectOrder()

    // Optimistic status: show immediate feedback while the API call is in flight
    this._failbackStatus = {
      state: AppStatusState.INDETERMINATE,
      text: 'Initiating failback...',
      issues: [],
    }
    // Rapid polling for 30s to cover async backend step function startup latency
    this.rapidRefreshExpires = new Date(Date.now() + 30000)

    this.launchFailbackSubscription = this.initiateFailback(launchEvent.applications)
      .pipe(finalize(() => (this.launchFailbackSubscription = null)))
      .subscribe(
        (_) => {
          this.showFailbackTab()
          this.ref.markForCheck()
          this.failbackDialog.close()
        },
        (err) => {
          // Revert optimistic status on failure
          this.recomputeTabStatuses()
          // The dialog only closes on success, so the user is still in it when this fires.
          handleArpioError(window.location.href, this.apiService, this.uiService, {
            errorContext: { dialogId: 'failback-dialog' },
          })(err)
        }
      )
  }

  dismissModal() {
    this.failoverDialog.close()
  }

  /**
   * `until-state-change` has no meaning to the host, which cannot know what this object's state
   * is. The component that owns the polling decides, and calls this when it moves.
   */
  private clearOverviewError() {
    this.overviewErrorHost?.clear()
  }

  onOverviewErrorAction(action: ErrorAction) {
    if (action.id === 'refresh') this.refreshApplication()
  }

  concludeRecoveryButtonClick() {
    this.concludeFailoverDialog.open()
  }

  failbackButtonClick() {
    this.failbackDialog.open()
  }

  unquarantineButtonClick() {
    this.unquarantineDialog.open()
  }

  concludeRecovery(concludeEvent: { restoreSettings: Array<LaunchRestoreSettings> }) {
    this.showRecoveryEnvironmentTab() // Failback tab will disappear, so make sure it's not active.
    this.setupOptimisticTransition('Concluding recovery...')
    this._isConcludingToStandby = true
    this._concludeBaselineRequest = this.application?.lastRestoreRequest?.getTime() ?? 0

    this.launchSyncSubscription = this.setApplicationSyncPhase(concludeEvent.restoreSettings, SyncPhaseDto.STANDBY)
      .pipe(finalize(() => (this.launchSyncSubscription = null)))
      .subscribe(
        (_) => {
          this.ref.markForCheck()
          this.concludeFailoverDialog.close()
        },
        (error) => {
          this.resetConcludeToStandbyBridge() // failed conclude never reaches standby — release the bridge
          this.revertOptimisticTransition() // Revert default optimistic status on failure
          handleArpioError(window.location.href, this.apiService, this.uiService, {
            errorContext: { dialogId: 'conclude-recovery-dialog' },
          })(error)
        }
      )
  }

  dismissConcludeModal() {
    this.concludeFailoverDialog.close()
  }

  launchTestButtonClick() {
    this.logger.info('User clicked test button - open test dialog', {
      component: 'OpenModal',
    })
    this.testFailoverDialog.open()
  }

  sandboxConfigButtonClick() {
    this.sandboxConfigDialog.open()
  }

  launchTest(testSettings: LaunchRestoreSettings[]) {
    this.logger.info('User clicked test button - launching test recovery', {
      component: 'TestButton',
    })
    this.setupOptimisticTransition('Initiating test recovery...')
    this.launchSyncSubscription = this.setApplicationSyncPhase(testSettings, SyncPhaseDto.FAILOVER_TEST)
      .pipe(finalize(() => (this.launchSyncSubscription = null)))
      .subscribe(
        (_) => {
          this.restoreLaunching = true
          this.showRecoveryEnvironmentTab()
          this.ref.markForCheck()
          this.testFailoverDialog.close()
          this.sandboxConfigDialog.close()
        },
        (error) => {
          this.revertOptimisticTransition() // Revert default optimistic status on failure
          // Either dialog can start a test, so the failure belongs in whichever one submitted it.
          const dialogId = this.sandboxConfigDialog.displayed ? 'sandbox-config-dialog' : 'launch-test-dialog'
          handleArpioError(window.location.href, this.apiService, this.uiService, {
            errorContext: { dialogId, objectId: 'application-overview' },
          })(error)
        }
      )
  }

  dismissLaunchTestModal() {
    this.testFailoverDialog.close()
  }

  dismissSandboxConfigModal() {
    this.sandboxConfigDialog.close()
  }

  get launchingSync() {
    return this._reportInFlight || subscriptionActive(this.launchSyncSubscription)
  }

  private _reportInFlight = false

  get launchingFailback() {
    return subscriptionActive(this.launchFailbackSubscription)
  }

  concludeTestButtonClick() {
    this.concludeTestFailoverDialog.open()
  }

  concludeTestFailover(concludeEvent: ConcludeTestSettings) {
    this._runConcludeTestFailover(concludeEvent)
  }

  private _generateAndDownloadRecoveryReport(concludeEvent: ConcludeTestSettings, next: () => void) {
    const accountId = this.accountService.accountId
    const reportAppIds = concludeEvent.restoreSettings
      .filter((rs) => rs.cancelledMidRestore !== true)
      .map((rs) => rs.application.applicationId)
    if (!accountId || reportAppIds.length === 0 || concludeEvent.reportTypes.length === 0) {
      next()
      return
    }
    const recoveryResult: 'success' | 'failed' = concludeEvent.successfulTest ? 'success' : 'failed'
    this.apiService
      .createRecoveryReport(
        accountId,
        reportAppIds,
        recoveryResult,
        concludeEvent.reportRecipients ?? [],
        concludeEvent.reportTypes
      )
      .pipe(first())
      .subscribe(
        ({ downloadUrl, pdfUrls }) => {
          const timestamp = new Date().toISOString().replace(/[:.]/g, '-')
          const downloads: Array<{ url: string; filename: string }> = []
          if (downloadUrl) {
            // One file even for two variants (zipped), so no multi-download prompt.
            downloads.push({
              url: downloadUrl,
              filename: `recovery-report-${timestamp}.${reportFileExtension(downloadUrl)}`,
            })
          } else {
            // Fallback: older APIs return a PDF per variant, so name each one distinctly.
            if (pdfUrls.summary) {
              downloads.push({ url: pdfUrls.summary, filename: `recovery-report-${timestamp}.pdf` })
            }
            if (pdfUrls.detailed) {
              downloads.push({ url: pdfUrls.detailed, filename: `recovery-report-detailed-${timestamp}.pdf` })
            }
          }
          // Sequentially, not in parallel — browsers throttle (and Chrome prompts on)
          // simultaneous automatic downloads from one gesture. Each link catches its own
          // failure so a dead presigned URL on the first PDF doesn't skip the second.
          const failures: string[] = []
          downloads
            .reduce(
              (chain, download) =>
                chain.then(() =>
                  this._downloadReportAsBlob(download.url, download.filename).catch((fetchError) => {
                    failures.push(fetchError?.message || `${download.filename} failed to download.`)
                  })
                ),
              Promise.resolve()
            )
            .then(() => {
              if (failures.length === 0) return
              // Async result of work the user kicked off, so it must not flash away.
              this.uiService.announceError({
                message:
                  failures.length === downloads.length
                    ? 'Recovery report could not be downloaded'
                    : 'Part of the recovery report could not be downloaded',
                detailedMessage: failures.join(' '),
                sticky: true,
              })
            })
            .finally(() => {
              next()
            })
        },
        (error) => {
          this.uiService.announceError({
            message: 'Recovery report could not be generated',
            detailedMessage:
              (error && (error.body?.message || error.message)) ||
              'The recovery report was not generated. The test was still concluded.',
            sticky: true,
          })
          next()
        }
      )
  }

  // Fetch a report file from its presigned S3 URL and hand the bytes to the browser as a
  // same-origin blob-URL download. Going through a blob rather than linking straight
  // at S3 is what makes `download` (and therefore the filename) work — the attribute
  // is ignored on cross-origin links.
  private _downloadReportAsBlob(presignedUrl: string, filename: string): Promise<void> {
    return fetch(presignedUrl)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Report fetch failed: ${response.status} ${response.statusText}`)
        }
        return response.arrayBuffer()
      })
      .then((bytes) => {
        const blob = new Blob([bytes])
        const objectUrl = URL.createObjectURL(blob)
        const anchor = document.createElement('a')
        anchor.href = objectUrl
        anchor.download = filename
        anchor.style.display = 'none'
        document.body.appendChild(anchor)
        try {
          anchor.click()
        } finally {
          document.body.removeChild(anchor)
          // Revoke asynchronously so the browser has time to start the download —
          // an immediate revoke can race with the click on some builds.
          setTimeout(() => URL.revokeObjectURL(objectUrl), 0)
        }
      })
  }

  private _runConcludeTestFailover(concludeEvent: ConcludeTestSettings) {
    this._concludingTest = true
    this._isConcludingToStandby = true
    this._concludeBaselineRequest = this.application?.lastRestoreRequest?.getTime() ?? 0
    this.setupOptimisticTransition('Concluding test...')
    const { restoreSettings, successfulTest, testNotes } = concludeEvent

    // Cancel any current application polling or refresh timers.  We'll re-schedule them when this process completes.
    this.cancelSubscriptionsInCorrectOrder()

    // build an array of result submissions for the selected applications.
    // fork-join with an empty list will never emit.  We need to at least emit a null.
    let applicationUpdates$ = [of<Application>(null)]
    if (restoreSettings.length > 0) {
      applicationUpdates$ = restoreSettings.map((rs) => {
        // ARP-7119 mixed-batch semantics: rows the dialog flagged as
        // cancel-bound (restore not yet complete) record the fixed cancel
        // note + false outcome regardless of what the user picked for the
        // concluded apps in the same submission. Matches the disclaimer's
        // promise that those apps "will be cancelled, not concluded."
        const isCancel = rs.cancelledMidRestore === true
        const rowSuccessful = isCancel ? false : successfulTest
        const rowNotes = isCancel ? 'Test cancelled before the restore completed.' : testNotes
        return rs.application.submitFailoverTestResults$(rowSuccessful, rowNotes).pipe(
          tap((application) => {
            if (application.applicationId === this.application.applicationId) {
              this.setApplication(application)
            }
          })
        )
      })
    }

    // We need to close the dialog after the sync is initiated, and show the spinny on the conclude button
    // between the button click and the dialog close.  To do this, we'll need 2 subscriptions against the
    // same underlying observable.  We'll use the shareReplay operator to build an observable that can
    // be subscribed to twice.

    const updateObservable = forkJoin(applicationUpdates$).pipe(
      switchMap((_applications) => {
        return Application.updateApplicationSyncPhases$(
          this.application,
          concludeEvent.restoreSettings,
          SyncPhaseDto.STANDBY
        )
      }),
      tap(() =>
        restoreSettings.forEach((rs) =>
          this.crossTabSync.notifyApplicationChanged(
            this.accountService.accountId!,
            rs.application.applicationId,
            SyncPhaseDto.STANDBY
          )
        )
      ),
      shareReplay({ bufferSize: 1, refCount: true })
    )

    // Now, subscribe to that observable for the first time.  This is the subscription that will
    // poll until the restore has completed.
    this.applicationSubscription = updateObservable
      .pipe(
        takeUntil(this.destroy$),
        switchMap((_) => this.application.refresh$(false)), // refreshDto=false on first poll: preserve the fresh DTO from initiateRestore to avoid a race where getApplication() returns stale data before the DB write completes
        tap((application) => this.pulseSpinnerIfActive(application)),
        catchError((error) => (error instanceof Http409Error ? this.application.refresh$(true) : throwError(error))),
        finalize(() => {
          this._concludingTest = false
          this.rescheduleApplicationRefresh()
          this.applicationSubscription = null
          this.isPolling = false
        })
      )
      .subscribe(
        (application) => {
          if (application && application.applicationId !== this.applicationId) {
            return
          }
          this.setApplication(application)
        },
        handleArpioError(window.location.href, this.apiService, this.uiService)
      )

    // Finally, subscribe to this observable again, using the launchSyncSubscription variable.
    // We want to know when the launch completes, not when the actual sync completes, so we just
    // want this subscription to last for 1 emission.
    this.launchSyncSubscription = updateObservable
      .pipe(
        first(),
        finalize(() => (this.launchSyncSubscription = null))
      )
      .subscribe(
        (_) => {
          // The concluding restore stores the attested result the report reads, so generate it only now.
          this._reportInFlight = true
          this._generateAndDownloadRecoveryReport(concludeEvent, () => {
            this._reportInFlight = false
            this.ref.markForCheck()
            this.concludeTestFailoverDialog.close()
          })
        },
        (error) => {
          this.resetConcludeToStandbyBridge() // failed conclude never reaches standby — release the bridge
          this.revertOptimisticTransition() // Revert default optimistic status on failure
          // Only this subscription is the submit. The one above it polls the sync that follows,
          // so its failures stay toasts rather than landing in a dialog the user has moved on from.
          handleArpioError(window.location.href, this.apiService, this.uiService, {
            errorContext: { dialogId: 'conclude-test-dialog' },
          })(error)
        }
      )
  }

  dismissConcludeTestModal() {
    this.concludeTestFailoverDialog.close()
  }

  showSandboxEventsDialog() {
    this.awsEventLogsDialog.open()
  }

  get rpoText(): string {
    return this.application?.rpo != null ? durationText(this.application.rpo) : ''
  }

  // True when the app is configured for real-time RPO (rpo === 0).
  // Used to surface an info affordance on the backup status bar, because the
  // backend schedules a new backup as soon as the last one finishes, making
  // "Creating backup..." the near-constant steady state.
  get isRealTimeRpo(): boolean {
    return this.application?.rpo === 0
  }

  get rtReplicationDocsUrl(): string {
    return this.uiService.rtReplicationDocsUrl
  }

  get latestTestTimeAgoString() {
    const result = this.application.latestFailoverTestResult
    if (result) {
      return timeAgoString(result.timestamp)
    } else {
      return result === null ? 'Never' : '—'
    }
  }

  /** Label for the test/recovery detail in the header strip. */
  get lastTestLabel(): string {
    const phase = this.application.syncPhase
    if (phase === SyncPhaseDto.FAILOVER_TEST) {
      return 'Active Test'
    } else if (phase === SyncPhaseDto.FAILOVER) {
      return 'Active Recovery'
    }
    return 'Last Test'
  }

  /** Value for the test/recovery detail in the header strip. */
  get lastTestValue(): string {
    const phase = this.application.syncPhase
    if (phase === SyncPhaseDto.FAILOVER_TEST || phase === SyncPhaseDto.FAILOVER) {
      const startTime = this.application.lastRestoreStart
      if (startTime) {
        return `Started at ${shortTimeString(startTime)}`
      }
      return 'Starting...'
    }
    // Optimistic text while a test conclude is in progress — bridges the gap
    // before the API has persisted the test result timestamp.
    if (this._concludingTest && this.isStandbyRestoreConcluding) {
      return 'Concluding...'
    }
    const result = this._displayedTestResult
    if (result) {
      return `${this.latestTestTimeAgoString} (${result.success ? 'successful' : 'failed'})`
    }
    return this.latestTestTimeAgoString
  }

  /** True only while the strip is showing a terminal test result that failed. */
  get lastTestFailed(): boolean {
    return this._displayedTestResult?.success === false
  }

  // The test result the strip is actually showing, or null when an active-restore or
  // concluding branch owns the value instead. Shared so the text and the failed styling
  // can never disagree about which branch is in effect.
  private get _displayedTestResult(): FailoverTestResultDto | null {
    const phase = this.application.syncPhase
    if (phase === SyncPhaseDto.FAILOVER_TEST || phase === SyncPhaseDto.FAILOVER) {
      return null
    }
    if (this._concludingTest && this.isStandbyRestoreConcluding) {
      return null
    }
    return this.application.latestFailoverTestResult ?? null
  }

  get isStandby() {
    return this.application.syncPhase === SyncPhaseDto.STANDBY
  }

  get isFailover() {
    return this.application.syncPhase === SyncPhaseDto.FAILOVER
  }

  get currentRecoveryPointTimeString(): string {
    const rp = this.application.currentRecoveryPoint
    return rp ? shortTimeString(rp.timestamp) : ''
  }

  constantTrackBy(index: number, item: any) {
    return 1
  }

  get showSyncActions() {
    return this.accountService.entitledForCapability('sync')
  }

  get failbackEnabled() {
    return this.accountService.entitledForCapability('failback')
  }

  get resourcesAreQuarantined(): boolean {
    return this._cachedResourcesAreQuarantined
  }

  dismissFailbackModal() {
    this.failbackDialog.close()
  }

  dismissUnquarantineModal() {
    this.unquarantineDialog.close()
  }

  showRecoveryEnvironmentTab() {
    this.switchTab(ResourcesTab.RECOVERY_ENVIRONMENT)
  }

  showBackupsTab() {
    this.switchTab(ResourcesTab.BACKUP)
  }

  showFailbackTab() {
    this.switchTab(ResourcesTab.FAILBACK)
  }

  // Bidirectional mapping between URL fragment strings and tab enum values
  private static readonly TAB_FRAGMENTS: Record<string, ResourcesTab> = {
    recovery: ResourcesTab.RECOVERY_ENVIRONMENT,
    backup: ResourcesTab.BACKUP,
    failback: ResourcesTab.FAILBACK,
    issues: ResourcesTab.ISSUES,
  }

  private static tabToFragment(tab: ResourcesTab): string | null {
    for (const [frag, t] of Object.entries(ApplicationComponent.TAB_FRAGMENTS)) {
      if (t === tab) return frag
    }
    return null
  }

  private static fragmentToTab(fragment: string): ResourcesTab | null {
    return ApplicationComponent.TAB_FRAGMENTS[fragment] ?? null
  }

  // Map tab to the API resource scope for scoped polling (see CLAUDE.md "Scoped Resource Loading")
  private tabToScope(tab: ResourcesTab): ResourceScope | null {
    switch (tab) {
      case ResourcesTab.RECOVERY_ENVIRONMENT:
        return 'restore'
      case ResourcesTab.BACKUP:
        return 'backup'
      case ResourcesTab.FAILBACK:
        return 'failback'
      default:
        return null
    }
  }

  /** Ride-along fetch the failback scope when the FAILBACK button is visible but its data isn't loaded.
   *  Without this, the button's [disabled] gate (driven by hasFailbackResources) shows "Loading..." until
   *  the user clicks into the Failback sub-tab — which is the only place that normally activates the scope.
   *  Returns true when the scope was newly added so callers can decide whether to force an immediate refresh. */
  private primeFailbackScopeIfNeeded(): boolean {
    if (!this.application) return false
    if (this.currentTab !== ResourcesTab.RECOVERY_ENVIRONMENT) return false
    if (!this.showFailback) return false
    if (this.application.isScopeLoaded('failback')) return false
    this.application.addActiveScope('failback')
    return true
  }

  /** Switch tab with a brief loading transition so PrimeNG table shows skeleton rows while initializing. */
  private switchTab(tab: ResourcesTab) {
    if (this.currentTab === tab) return
    this.tabTransitionLoading = true
    this.currentTab = tab

    // Update active scope for resource loading
    const scope = this.tabToScope(tab)
    if (scope && this.application) {
      this.application.setActiveScope(scope)
      // FAILBACK button lives in the Recovery Env tab bar and depends on failback resources;
      // prime the scope here so the button reflects accurate state without requiring a Failback sub-tab visit.
      const primedFailback = this.primeFailbackScopeIfNeeded()
      if (!this.application.isScopeLoaded(scope) || primedFailback) {
        // Trigger immediate refresh to load the new scope's resources
        this.cancelSubscriptionsInCorrectOrder()
        this.refreshApplication()
      } else {
        this.refreshResourceCache()
      }
    }

    // Update the URL fragment so a page reload returns to the correct tab
    const fragment = ApplicationComponent.tabToFragment(tab)
    this.router.navigate([], { relativeTo: this.route, fragment, replaceUrl: true })
    // Only clear transition loading immediately if scope is already loaded;
    // otherwise setApplication() will clear it when resources arrive
    if (!scope || !this.application || this.application.isScopeLoaded(scope)) {
      setTimeout(() => {
        this.tabTransitionLoading = false
        this.cancelTabTransitionTimeout()
        this.ref.markForCheck()
      }, 0)
    } else {
      // Safety net: clear after 5s if resources don't arrive
      this.cancelTabTransitionTimeout()
      this.tabTransitionTimeoutSubscription = timer(5000).subscribe(() => {
        if (this.tabTransitionLoading) {
          this.tabTransitionLoading = false
          this.ref.markForCheck()
        }
        this.tabTransitionTimeoutSubscription = null
      })
    }
  }

  get activeTab(): ResourcesTab {
    return this.currentTab
  }

  /** Absolute link to the configure page, works from both base and /issues routes */
  get configureLink(): string[] {
    const accountId = this.accountService.accountId
    return ['/', accountId, 'applications', this.application.applicationId, 'configure']
  }

  /** Number of resources currently cached for the active tab (cheap check, no recompute). */
  private getActiveTabResourceCount(): number {
    switch (this.currentTab) {
      case ResourcesTab.BACKUP:
        return this.cachedBackupResources?.length || 0
      case ResourcesTab.RECOVERY_ENVIRONMENT:
        return this.cachedRecoveryResources?.length || 0
      case ResourcesTab.FAILBACK:
        return this.cachedFailbackResources?.length || 0
      default:
        return 0
    }
  }

  get statusContext(): StatusContext {
    return this.currentTab === ResourcesTab.BACKUP ? StatusContext.BACKUP : StatusContext.RECOVERY
  }

  get showFailback() {
    return this.failbackEnabled && this.isFailover && !this.resourcesAreQuarantined
  }

  get hasFailbackResources(): boolean {
    return this._cachedHasFailbackResources
  }

  // Tooltip for the disabled FAILBACK button — distinguishes "still loading" from
  // "this recovery point has nothing that can be failed back". Returns null when the
  // button is enabled (no tooltip).
  get failbackButtonDisabledTooltip(): string | null {
    if (this._cachedHasFailbackResources) return null
    if (!this.application?.isScopeLoaded('failback')) return 'Loading failback resources…'
    return 'No resources in this recovery point support failback'
  }

  doFilterResources(filterText: string) {
    // Persist the filter text so it survives tab switches and component recreation.
    // Do NOT call refreshResourceCache() here — the child component already applies
    // the filter client-side via PrimeNG's filterGlobal(). Re-fetching resources from
    // the parent causes a full table re-render (new array reference → ngOnChanges),
    // which is the source of the ~8s delay on large resource sets.
    this.filterText = filterText
  }

  // Programmatically apply a "Failed" status filter on the target tab's resource table
  filterToFailedResources(tab: 'recovery' | 'backup' | 'failback') {
    const view =
      tab === 'recovery'
        ? this.recoveryResourcesView
        : tab === 'failback'
          ? this.failbackResourcesView
          : this.backupResourcesView
    if (view) {
      view.setStatusFilter(ResourceStatus.FAILED)
    }
  }

  /** Returns the upstream-blocked count (NOT_PROCESSED due to upstream failure) for the target
   *  tab's resource view, or null when the notice shouldn't be shown. Used by the status bar to
   *  render a substatus line linking to FAILED resources. */
  upstreamBlockedCountFor(tab: 'recovery' | 'backup' | 'failback'): number | null {
    const view =
      tab === 'recovery' ? this.recoveryResourcesView : tab === 'failback' ? this.failbackResourcesView : null
    if (!view?.showUpstreamFailureNotice) return null
    return view.upstreamBlockedCount
  }

  // Export every issue across backup + recovery + failback scopes as CSV.
  // Triggered from the Issues tab toolbar link. Matches the per-issue fields shown
  // in the UI so users can diff against what they see on screen.
  exportAllIssuesToCsv(): void {
    const rows: ViewableIssue[] = [
      ...this.backupIssues.map((i) => ({ ...i, _scope: 'Backup' }) as ViewableIssue & { _scope: string }),
      ...this.restoreIssues.map((i) => ({ ...i, _scope: 'Recovery' }) as ViewableIssue & { _scope: string }),
      ...this.failbackIssues.map((i) => ({ ...i, _scope: 'Failback' }) as ViewableIssue & { _scope: string }),
    ]
    if (rows.length === 0) return

    const headers = ['Scope', 'Resource Type', 'Resource Name', 'ARN', 'Issue Type', 'Message', 'Timestamp']
    // Mirror the on-screen fallbacks from issue-content-group: type uses the human display
    // string, name derives from the ARN when the issue didn't match a known resource. Without
    // this, sub-resources whose ARN doesn't resolve to an ApplicationResourceDto (e.g. some
    // VPC endpoints and subnets) export blank Type/Name even though the UI shows them.
    const csvRows = rows.map((r) => [
      (r as any)._scope,
      r.resourceType ? getResourceTypeDisplayString(r.resourceType, r.resourceVariant) : '',
      r.resourceName || getResourceNameFromArn(r.arn) || '',
      r.arn || '',
      r.issue?.issueTypeDisplayString || '',
      r.issue?.message || '',
      r.timestamp ? new Date(r.timestamp).toISOString() : '',
    ])

    // BOM prefix ensures Excel opens UTF-8 CSVs with proper encoding
    const csvContent =
      '\uFEFF' +
      [headers, ...csvRows]
        .map((row) => row.map((cell) => `"${(cell ?? '').toString().replace(/"/g, '""')}"`).join(','))
        .join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const appName = this.application?.name ? this.application.name.replace(/[^a-zA-Z0-9-_]/g, '_') : 'application'
    const timestamp = new Date().toISOString().split('T')[0]
    link.href = URL.createObjectURL(blob)
    link.download = `${appName}-issues-${timestamp}.csv`
    link.click()
    URL.revokeObjectURL(link.href)
  }

  // Switch to the given tab and filter to a specific resource (used from issue detail links)
  navigateToResourceInTab(tab: 'recovery' | 'backup' | 'failback', resourceName: string) {
    if (!resourceName) return
    // Update filterText BEFORE switching tabs so the destination view — which is recreated via
    // *ngIf on activeTab — receives the new value as [initialFilterText]. Otherwise the child's
    // ngAfterViewInit setTimeout reads the stale persisted filterText and overwrites the parent's
    // post-switch setTimeout below, causing the search bar to flash the resource name then revert.
    this.filterText = resourceName
    if (tab === 'backup') {
      this.showBackupsTab()
    } else if (tab === 'failback') {
      this.showFailbackTab()
    } else {
      this.showRecoveryEnvironmentTab()
    }
    // Covers the "tab already active" path where the child is not recreated and initialFilterText
    // doesn't re-fire. Both timeouts now apply the same value, so the destroy/recreate race is gone.
    setTimeout(() => {
      const view =
        tab === 'recovery'
          ? this.recoveryResourcesView
          : tab === 'failback'
            ? this.failbackResourcesView
            : this.backupResourcesView
      if (view) {
        view.transferkeywordToAllApplications(resourceName)
      }
    })
  }

  get applicationSyncPhase() {
    return this.application.syncPhase
  }

  get showNetworkSandboxStatus() {
    return (
      this.accountService.entitledForCapability('networkSandbox') &&
      this.application.syncPhase == SyncPhaseDto.FAILOVER_TEST
    )
  }

  get showSandboxSettingsButton(): boolean {
    return (
      this.application.syncPhase == SyncPhaseDto.FAILOVER_TEST &&
      this.application.networkSandboxEnabled &&
      !this.appIsDeleted &&
      this.canTest
    )
  }

  get showRansomwareRecoveryStatus() {
    return this.accountService.quarantineEnabled && this.application.syncPhase === SyncPhaseDto.FAILOVER
  }

  get networkSandboxStatus() {
    return this.application.networkSandboxEnabled ? 'enabled' : 'disabled'
  }

  // Whether the operation-status notice has anything to show when expanded.
  // Drives the chevron visibility and the click-to-toggle behavior — if there
  // are no detail rows, the notice shouldn't look expandable.
  get operationNoticeHasDetails(): boolean {
    if (!this.application || !this.showNetworkSandboxStatus) return false
    const hasDomains =
      !!this.application.advancedNetworkSandboxEnabled && !!this.application.networkSandboxAllowedDomains?.length
    const hasWhitelist =
      !!this.accountService.networkSandboxWhitelistEnabled && !!this.application.networkSandboxWhitelist?.length
    return hasDomains || hasWhitelist
  }

  // Text for the Failback sub-tab's operation notice. Mirrors the Recovery tab pattern
  // of a state-aware notice rather than a hardcoded string. Returns null when there's
  // nothing useful to add on top of the status bar (loading, idle with nothing to do, etc.).
  get failbackOperationNoticeText(): string | null {
    if (!this.application || !this.isFailover) return null
    if (!this.application.isScopeLoaded('failback')) return null
    if (this.application.currentlyFailingBack) {
      return 'Failback is returning resources to the primary environment.'
    }
    const lastSuccess = this.application.lastFailbackSuccess
    const lastFailure = this.application.lastFailbackFailure
    if (lastSuccess && (!lastFailure || lastSuccess >= lastFailure)) {
      return 'Failback complete. Resources have been returned to the primary environment.'
    }
    return null
  }

  // Toggle state for expandable sandbox config sections in the header strip
  domainAllowlistExpanded = false
  whitelistExpanded = false
  operationNoticeCollapsed = true

  domainAllowlistTruncated = false
  whitelistTruncated = false
  private _allowlistTruncationChecked = { domain: false, whitelist: false }

  // No overflow, no expand affordance. Lazy on first hover, like onTruncatableHover.
  checkAllowlistTruncation(which: 'domain' | 'whitelist', event: Event): void {
    if (this._allowlistTruncationChecked[which]) return
    const expanded = which === 'domain' ? this.domainAllowlistExpanded : this.whitelistExpanded
    if (expanded) return
    const textEl = (event.currentTarget as HTMLElement).querySelector('.allowlist-text') as HTMLElement | null
    if (!textEl) return
    this._allowlistTruncationChecked[which] = true
    const truncated = textEl.scrollWidth > textEl.clientWidth
    if (which === 'domain') this.domainAllowlistTruncated = truncated
    else this.whitelistTruncated = truncated
  }

  get domainAllowlistStatus(): string {
    if (this.application.networkSandboxEnabled && this.application.networkSandboxAllowedDomains) {
      return `Outbound access domains: ${this.application.networkSandboxAllowedDomains.join(', ')}`
    } else {
      return ''
    }
  }

  get whitelistStatus(): string {
    if (this.application.networkSandboxEnabled && this.application.networkSandboxWhitelist) {
      return `Outbound access CIDRs: ${this.application.networkSandboxWhitelist.join(', ')}`
    } else {
      return ''
    }
  }

  get ransomwareRecoveryStatus() {
    return this.application.ransomwareRecoveryEnabled ? 'enabled' : 'disabled'
  }

  get lastFailback(): ProcessingStatus {
    return this._cachedLastFailback
  }

  get lastBackupStatus(): ProcessingStatus {
    return this._cachedLastBackupStatus
  }

  get lastRestoreStatus(): ProcessingStatus {
    return this._cachedLastRestoreStatus
  }

  get lastSyncComplete(): Date {
    return this.application.lastRestoreComplete
  }

  // True once recovery point attempts have been fetched from the API (null = not yet loaded)
  get recoveryPointAttemptsLoaded(): boolean {
    return !!this.application && this.application.recoveryPointAttempts !== null
  }

  // True when recovery point attempts have loaded AND contain data for the chart
  get hasChartData(): boolean {
    return this.recoveryPointAttemptsLoaded && this.recoveryPointAttempts.length > 0
  }

  get appResourcesLoaded(): boolean {
    return this.application.isLoaded
  }

  get currentlyRestoring(): boolean {
    return this.application.currentlyRestoring
  }

  // True while the test is in flight and the restore attempt hasn't completed —
  // gates the "Cancel test" early-stop variant of the conclude button (ARP-6708).
  // Latched on the application so it doesn't flicker between attempts; once the first
  // restore finishes (success or failure), we stay in the regular Conclude test flow
  // for the rest of the cycle.
  get isEarlyStopTest(): boolean {
    return this.applicationSyncPhase === SyncPhaseDto.FAILOVER_TEST && !this.application?.firstRestoreCompletedInTest
  }

  // Azure can't cancel an in-flight restore yet, so it's intentionally prevented in the
  // UI: callers disable the conclude/cancel buttons while one is active.
  private isAzureRestoreInFlight(phase: SyncPhaseDto): boolean {
    return (
      this.application?.isAzure === true &&
      this.applicationSyncPhase === phase &&
      this.application.currentlyRestoring === true
    )
  }

  /**
   * Reason the per-phase action buttons should render disabled, or null if
   * actions are available. Returning a string disables the buttons and feeds
   * the tooltip — see ARP-7119: bare spinners gave no actionable context;
   * disabled buttons + tooltip explain why the user has to wait.
   */

  get disabledActionReason(): string | null {
    if (!this.application) return null
    if (this.application.currentlyFailingBack) {
      return 'A failback is in progress — actions are unavailable until it completes.'
    }
    if (this.isConcludingToStandby) {
      return this.concludingToStandbyTooltip
    }
    // Restore spans the region-pair group, so this app can read 100% while
    // siblings finish. Disable rather than hide so the buttons never vanish.
    if (this.isAzureRestoreInFlight(SyncPhaseDto.FAILOVER_TEST) || this.isAzureRestoreInFlight(SyncPhaseDto.FAILOVER)) {
      return 'A recovery is still in progress for other apps with the same primary and recovery regions. This action will be available once all of its applications finish restoring.'
    }
    // FAILOVER_TEST keeps its buttons enabled during restore — the conclude
    // dialog itself handles the cancel-vs-conclude routing per app.
    if (this.application.syncPhase === SyncPhaseDto.FAILOVER_TEST) {
      return null
    }
    if (this.application.currentlyRestoring) {
      if (this.application.syncPhase === SyncPhaseDto.STANDBY) {
        // appGroupRestore exists precisely so users can queue actions against
        // a busy STANDBY app during a refresh restore — keep buttons enabled.
        if (this.accountService.entitledForCapability('appGroupRestore')) {
          return null
        }
        return 'A standby refresh is in progress — actions are unavailable until it completes.'
      }
      return 'A restore is in progress — actions are unavailable until it completes.'
    }
    return null
  }

  get hasDisabledActionReason(): boolean {
    return !!this.disabledActionReason
  }

  get currentlyFailingBack() {
    return this.application.currentlyFailingBack
  }

  get currentlyActive(): boolean {
    return this.application.currentlyActive
  }

  get nothingActive(): boolean {
    return !(
      subscriptionActive(this.applicationSubscription) || subscriptionActive(this.applicationRefreshTimerSubscription)
    )
  }

  get allActive(): boolean {
    return (
      subscriptionActive(this.applicationSubscription) && subscriptionActive(this.applicationRefreshTimerSubscription)
    )
  }

  private noSyncPairAppsInSyncPhase(syncPhase: SyncPhaseDto) {
    return this.applicationProviderService
      .loadedApplications()
      .filter((app) => Application.sameSyncPair(this.application, app))
      .reduce((accum, app) => accum && app.syncPhase !== syncPhase, true)
  }

  // Backend's automatic-restore scheduler skips a region pair when any sibling isn't STANDBY
  // (plan_restore.exec_plan_restore). Surface that wait when a restore would otherwise be imminent.
  private computeStandbyRestoreBlockedBySibling(): { sourceRegion: string; targetRegion: string } | undefined {
    const app = this.application
    if (!app || app.syncPhase !== SyncPhaseDto.STANDBY) return undefined
    if (!app.sourceRegion || !app.targetRegion) return undefined
    const hasPendingRestore =
      !app.lastRestoreSuccess || (!!app.lastBackupSuccess && app.lastBackupSuccess > app.lastRestoreSuccess)
    if (!hasPendingRestore) return undefined
    const blocking = this.applicationProviderService
      .loadedApplications()
      .find(
        (other) =>
          other.applicationId !== app.applicationId &&
          Application.sameRegionPair(app, other) &&
          (other.syncPhase === SyncPhaseDto.FAILOVER || other.syncPhase === SyncPhaseDto.FAILOVER_TEST)
      )
    if (!blocking) return undefined
    return { sourceRegion: app.sourceRegion, targetRegion: app.targetRegion }
  }

  // Permission-only (controls visibility). In-flight restores disable via
  // disabledActionReason.
  get canTest(): boolean {
    return this.canTestPermission
  }

  get canRecover(): boolean {
    return this.canRecoverPermission
  }

  get canManage(): boolean {
    return this.canManagePermission
  }

  get rbacEnabled(): boolean {
    return this.accountService.rbacEnabled
  }

  // This gets the deleted property on the curren applicationDto
  get appIsDeleted(): boolean {
    return this.application?.dto?.deleted
  }

  // After deletion, the backend tracks whether restore resources still remain
  get allResourcesCleaned(): boolean {
    return this.application?.lastRestoreResourcesRemain === false
  }

  /**
   * Checks if a manual standby restore is in progress to disable actions
   * like launching tests or recovery, preventing conflicts.
   * Returns true if:
   * - A restore is active (`application.currentlyRestoring`).
   * - The sync phase is `STANDBY` (`application.syncPhase`).
   * - The restore was triggered manually (`application.restoreTrigger === RestoreTrigger.MANUAL`).
   */
  get isStandbyRestoreConcluding(): boolean {
    return (
      this.application?.currentlyRestoring &&
      this.application?.syncPhase === SyncPhaseDto.STANDBY &&
      this.application?.restoreTrigger === RestoreTriggerDto.MANUAL
    )
  }

  // True from the moment a conclude click fires until the restore-to-standby
  // signal takes over. Bridges the gap where syncPhase has flipped to STANDBY
  // via the optimistic override but the new DTO hasn't yet populated
  // restoreTrigger=MANUAL, so the standby buttons would briefly render as
  // enabled before isStandbyRestoreConcluding fires.
  private _isConcludingToStandby = false
  // Baseline lastRestoreRequest captured at conclude click. The bridge clears
  // only once a DTO with a strictly newer lastRestoreRequest arrives — guards
  // against eventual-consistency replica reads that return pre-click data.
  private _concludeBaselineRequest = 0

  get isConcludingToStandby(): boolean {
    return this._isConcludingToStandby || this.isStandbyRestoreConcluding
  }

  // Drop the conclude-to-standby bridge. The success path clears it via the
  // baseline-guarded check in setApplication; this covers the paths that never
  // produce a clearing DTO — a failed conclude (error handlers) and teardown /
  // app-switch (cleanup) — so the bridge can't latch the buttons disabled and
  // pin the 2s poll cadence on a stale or unrelated application.
  private resetConcludeToStandbyBridge(): void {
    this._isConcludingToStandby = false
    this._concludeBaselineRequest = 0
    this._concludingTest = false
  }

  get concludingToStandbyTooltip(): string | null {
    return this.isConcludingToStandby
      ? 'This application is being restored back to standby — this action will be available once the restore completes.'
      : null
  }

  /** True when a standby restore (automatic or manual) is actively in progress. */
  get isStandbyRestoreActive(): boolean {
    return this.application?.currentlyRestoring && this.application?.syncPhase === SyncPhaseDto.STANDBY
  }

  /**
   * Determines if an application's deletion process has been completed.
   * An application is considered fully deleted when the backend reports
   * that no restore resources remain after the last restore.
   */
  private hasCompletedDeletionProcess(application: any): boolean {
    if (!application?.dto?.deletedAt) {
      return false
    }

    return application.lastRestoreResourcesRemain === false
  }

  // --- Issues tab ---

  private _launchingBackup = false
  private _launchingRestore = false
  // When true, preserve the optimistic "Planning backup..." status until the backend
  // reports actual backup activity. Set after editor save navigates back.
  private _expectingBackupToStart = false
  // When true, preserve the optimistic "Initiating restore..." status until the backend
  // reports the restore is actually in progress. Without this the next recompute reads
  // the still-FAILED cached resources and the Early Terminal Override snaps the headline
  // back to the prior failure for a few seconds. Set on resume/retry restore.
  private _expectingRestoreToStart = false
  // Optimistic flag: set when user concludes a test, cleared when the conclude
  // restore finishes. Lets lastTestValue show "Concluding..." instead of a
  // potentially stale/missing timestamp during the API race window.
  private _concludingTest = false

  get backupIssues(): ViewableIssue[] {
    return this._cachedBackupIssues
  }

  get restoreIssues(): ViewableIssue[] {
    return this._cachedRestoreIssues
  }

  get failbackIssues(): ViewableIssue[] {
    return this._cachedFailbackIssues
  }

  get hasIssues(): boolean {
    return this._cachedHasIssues
  }

  get totalIssueCount(): number {
    return this._cachedTotalIssueCount
  }

  // Gate the Issues tab render until grouping can run against enriched issues.
  // makeViewableIssues strips resource names to build grouping keys, so it needs the
  // resource DTO lookup populated; on first load issues arrive before it is, which would
  // render each issue in its own group and then visibly snap into groups once resources
  // load. Holding the loading spinner until enrichment is ready avoids that flash.
  get issuesReadyToRender(): boolean {
    if (this.totalIssueCount === 0) return false
    const app = this.application
    if (!app) return false
    // The lookup fills as any scope's resources load; once non-empty, enrichment works.
    if (app.allKnownResourceDtos.length > 0) return true
    // Fallback so an app with issues but no resources still renders: a completed scope
    // fetch (isScopeLoaded is true even for an empty list) means enrichment is as ready
    // as it will get.
    return app.isScopeLoaded('backup') || app.isScopeLoaded('restore') || app.isScopeLoaded('failback')
  }

  // Switch to the Issues tab. When a scope is provided (from a tab-status "Review and fix N issues"
  // link), queue a scroll to that scope's section heading without auto-opening any issues —
  // auto-expanding every group produced an overwhelming wall of open accordions.
  showIssuesTab(scope?: 'backup' | 'recovery' | 'failback') {
    this.switchTab(ResourcesTab.ISSUES)
    if (scope) {
      this.pendingScrollToScope = scope
      this.hasScrolledToScope = false
      this.scrollToScopeAttempts = 0
    }
  }

  // Retry deferred issue scroll on each CD cycle until the DOM element exists or max attempts reached
  ngAfterViewChecked(): void {
    if (this.pendingScrollToIssueId && !this.hasScrolledToIssue && this.currentTab === ResourcesTab.ISSUES) {
      // Guard against infinite DOM queries if the target element never appears
      if (++this.scrollToIssueAttempts > 50) {
        this.pendingScrollToIssueId = null
        this.scrollToIssueAttempts = 0
        return
      }
      this.scrollToIssue(this.pendingScrollToIssueId)
    }
    if (this.pendingScrollToScope && !this.hasScrolledToScope && this.currentTab === ResourcesTab.ISSUES) {
      if (++this.scrollToScopeAttempts > 50) {
        this.pendingScrollToScope = null
        this.scrollToScopeAttempts = 0
        return
      }
      const el = document.getElementById(`issues-section-${this.pendingScrollToScope}`)
      if (el) {
        this.hasScrolledToScope = true
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
        // Single-pulse highlight on the section title so the eye lands on the right scope.
        const label = el.querySelector('.section-label') as HTMLElement | null
        if (label) {
          label.classList.add('highlight-section')
          setTimeout(() => label.classList.remove('highlight-section'), 3000)
        }
        this.pendingScrollToScope = null
      }
    }
  }

  /**
   * Resolve the scrollable/highlightable element for a given issue ID.
   * For <issue> components, the host element itself has id="issue-xxx".
   * For <issue-content-group>, the host has the first issue's ID; secondary
   * issues use inner anchor <div>s — walk up to the host for those.
   */
  private resolveIssueElement(issueId: string): HTMLElement | null {
    const el = document.getElementById(`issue-${issueId}`)
    if (!el) return null
    // If the element is an anchor div inside a content-group, resolve to the host
    if (el.classList.contains('issue-anchor')) {
      return el.closest('issue-content-group') as HTMLElement
    }
    return el
  }

  private scrollToIssue(issueId: string): void {
    const element = this.resolveIssueElement(issueId)
    if (element) {
      this.hasScrolledToIssue = true

      // Collect all issue IDs to highlight (from multi-issue resource click, or just the single one)
      const idsToHighlight = this.pendingHighlightIssueIds.length > 0 ? this.pendingHighlightIssueIds : [issueId]

      // Expand parent groups for all highlighted issues
      let needsExpansion = false
      for (const id of idsToHighlight) {
        const el = this.resolveIssueElement(id)
        if (!el) continue
        const groupBody = el.closest('.group-body')
        if (groupBody) {
          const issueGroup = groupBody.closest('.issue-group')
          if (issueGroup) {
            const groupHeader = issueGroup.querySelector('.group-header') as HTMLElement
            if (groupHeader && !groupHeader.classList.contains('expanded')) {
              groupHeader.click()
              needsExpansion = true
            }
          }
        }
      }

      // Wait for accordion animation (200ms) to complete before scrolling,
      // otherwise the element is still zero-height and scrollIntoView fails.
      const delay = needsExpansion ? 250 : 0
      setTimeout(() => {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' })

        // Highlight all related issues, expand their accordions.
        // De-duplicate elements so multi-issue content groups only highlight once.
        const seen = new Set<HTMLElement>()
        for (const id of idsToHighlight) {
          const el = this.resolveIssueElement(id)
          if (!el || seen.has(el)) continue
          seen.add(el)
          el.classList.add('highlight-issue')
          setTimeout(() => el.classList.remove('highlight-issue'), 2000)
          const accordionButton = el.querySelector('.accordion') as HTMLElement
          if (accordionButton && !accordionButton.classList.contains('expanded')) {
            accordionButton.click()
          }
        }
        this.pendingHighlightIssueIds = []
      }, delay)
    }
  }

  get backingUp(): boolean {
    return this._launchingBackup || this.application?.currentlyBackingUp
  }

  get restoring(): boolean {
    return this._launchingRestore || this.application?.currentlyRestoring
  }

  // Feature-flagged: resume only when the backend has a lastJobId AND reports the prior job is
  // resumable. resumeRecoveryStatus.available === false (e.g. cause: "compatibility" after the
  // recovery point changed) means resume isn't possible, so we fall back to a full "Retry Restore".
  // An absent resumeRecoveryStatus is treated as resumable to preserve prior behavior.
  get canTryAgainFromJob(): boolean {
    if (!this.accountService.jobsTryAgainEnabled || !this.application?.dto?.restoreStatus?.lastJobId) {
      return false
    }
    const resumeStatus = this.application.dto.resumeRecoveryStatus
    return !resumeStatus || resumeStatus.available !== false
  }

  // Permission varies by sync phase: FAILOVER needs canRecover, FAILOVER_TEST needs canTest, STANDBY needs both
  getRestorePermission(): boolean {
    if (!this.application) return false
    if (this.application.syncPhase === SyncPhaseDto.FAILOVER) {
      return this.canRecover
    } else if (this.application.syncPhase === SyncPhaseDto.FAILOVER_TEST) {
      return this.canTest
    }
    return this.canTest && this.canRecover
  }

  launchBackupToTryAgain(): void {
    this._launchingBackup = true
    this.switchTab(ResourcesTab.BACKUP)
    // Cancel AFTER switchTab: switchTab may start its own refresh when the scope isn't loaded,
    // which would orphan a subscription that overwrites our optimistic status with stale data.
    this.cancelSubscriptionsInCorrectOrder()

    // Show default optimistic status immediately so the user sees feedback before the API responds.
    // Without this, the status bar shows stale "Failed" text for up to 15s (the backup poll delay).
    this._backupHealthStatus = { state: AppStatusState.INDETERMINATE, text: 'Initiating backup...', issues: [] }

    // Guarantee rapid polling (2s) for 30s after user action, even if the backend hasn't
    // transitioned to an active state yet (async step function startup latency).
    this.rapidRefreshExpires = new Date(Date.now() + 30000)

    // initiateBackup$() now emits twice: first immediately with the updated DTO from the
    // API response (replacing the optimistic text with real computed status), then again
    // after the polling delay with refreshed data from the server.
    this.applicationSubscription = this.application
      .initiateBackup$()
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.applicationSubscription = null
          this.rescheduleApplicationRefresh()
        })
      )
      .subscribe(
        (application) => {
          if (application && application.applicationId !== this.applicationId) {
            return
          }
          this._launchingBackup = false
          this.setApplication(application)
        },
        (error) => {
          this._launchingBackup = false
          this.recomputeTabStatuses() // Revert optimistic status on failure
          handleArpioError(window.location.href, this.apiService, this.uiService)(error)
        }
      )
  }

  launchRestoreToTryAgain(jobsTryAgain: boolean = true): void {
    this._launchingRestore = true

    // Navigate to Recovery Environment tab so the user can see the restore progress
    this.switchTab(ResourcesTab.RECOVERY_ENVIRONMENT)
    // Cancel AFTER switchTab: if the restore scope wasn't loaded, switchTab starts a refresh
    // that would race with our optimistic status and overwrite it with stale data.
    this.cancelSubscriptionsInCorrectOrder()

    // Optimistic status: show "Initiating" immediately so user sees feedback before API responds.
    this._recoveryEnvStatus = {
      state: AppStatusState.INDETERMINATE,
      text: 'Initiating restore...',
      issues: [],
    }
    // Preserve the optimistic status across recomputes until the restore visibly starts moving —
    // otherwise the still-FAILED cached resources snap the headline back to the prior failure.
    // Cleared in recomputeTabStatuses() once a resource enters PENDING/PROCESSING or rapid-refresh expires.
    this._expectingRestoreToStart = true
    // Guarantee 30s of rapid polling to cover async backend step function startup latency.
    this.rapidRefreshExpires = new Date(Date.now() + 30000)

    let restoreObservable
    if (jobsTryAgain && this.canTryAgainFromJob) {
      restoreObservable = this.application.initiateRestoreFromJob$()
    } else {
      const useLatestRecoveryPoint = this.application.deleted
      restoreObservable = this.application.initiateRestore$(
        useLatestRecoveryPoint,
        /* forceNewTargetPlan */ false,
        RestoreTypeDto.RETRY
      )
    }

    // Restore service methods now emit twice: first immediately with the API response DTO
    // (replacing the optimistic text with real computed status), then after delayed polling.
    this.applicationSubscription = restoreObservable
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.applicationSubscription = null
          this.rescheduleApplicationRefresh()
        })
      )
      .subscribe(
        (application) => {
          if (application && application.applicationId !== this.applicationId) {
            return
          }
          this._launchingRestore = false
          this.setApplication(application)
        },
        (error) => {
          this._launchingRestore = false
          this._expectingRestoreToStart = false // Clear so recompute reverts to the real status
          this.recomputeTabStatuses() // Revert optimistic status on failure
          handleArpioError(window.location.href, this.apiService, this.uiService)(error)
        }
      )
  }

  // PrimeNG SplitButton menu: "Resume" picks up from last failure, "Retry" restarts from scratch
  restoreMenuItems: MenuItem[] = [
    {
      label: 'Resume Restore<div class="menu-desc">Pick up the restore from the point of failure</div>',
      escape: false,
      command: () => this.launchRestoreToTryAgain(true),
    },
    {
      label: 'Retry Restore<div class="menu-desc">Restart the entire restore from the beginning</div>',
      escape: false,
      command: () => this.launchRestoreToTryAgain(false),
    },
  ]

  get sourceDescription(): string {
    return this.formatEndpointDescription(this.application?.sourceEndpointUri, this.application?.sourceSubscriptionName)
  }

  get targetDescription(): string {
    return this.formatEndpointDescription(this.application?.targetEndpointUri, this.application?.targetSubscriptionName)
  }

  // Extract account ID from endpoint URI format "provider:accountId:region"
  get sourceAccountId(): string {
    return this.application?.sourceEndpointUri?.split(':')[1] || ''
  }

  get targetAccountId(): string {
    return this.application?.targetEndpointUri?.split(':')[1] || ''
  }

  onAccountIdCopied(which: 'source' | 'target') {
    this.copyFeedback.onCopied(which, this.ref)
  }

  private formatEndpointDescription(endpointUri: string, subscriptionName: string | null): string {
    const [, accountId, region] = endpointUri.split(':')
    return subscriptionName ? `${subscriptionName} (${accountId}) / ${region}` : `${accountId} / ${region}`
  }
}
